/**
 * @file        bridge-trainer-v88.jsx
 * @description Acol bridge bidding trainer. Presents 3 hands sharing a common
 *              auction context; player bids all three before receiving feedback.
 *              Covers uncontested and competitive auctions per EBU Red Book /
 *              Beginning Bridge. Conventions: Stayman and Blackwood only.
 *              LLM Coach Phase 2: Netlify function, per-set and ask-coach API
 *              calls, focus directive, prompt caching.
 * @author      Claude (Anthropic) with David Shaffer
 * @version     v93
 * @date        2026-04-19 BST
 * @changes     v93: fix Buy Me a Coffee button — replace dynamic script injection (fails because document.currentScript is null for programmatically created script elements) with static image link.
 *              v90: EBU BB1 textbook tests integrated — 75 tests from bb1_test_cases.txt added to test_engine.js (220 total); detOpen signature updated with dealer param (3rd arg, "S" default), noWeakTwo shifted to 4th; 6 textbook failures identified as engine gaps (R004c, R018, R023, R026, R028, R033b) to be fixed separately.
 *              v89: BB1/BB2 weak two and pre-empt filter — belt-and-braces approach: (belt) bb12Ex filter now applied in fallback genQ loop at all 3 call sites so a filter miss in the primary loop cannot allow weak two/pre-empt hands to slip through; (braces) detOpen gains noWeakTwo flag — if the hand qualifies for a weak two or pre-empt and noWeakTwo=true, Pass is returned with explanation "At this stage we haven't covered weak twos or pre-emptive openings"; noWeakTwo threaded from genQ through gOpen for bb1/bb2 focus, mirroring noStayman; 4 new detOpen noWeakTwo tests (145 total).
 *              v88: jump shift whyNot/describeBidInContext request text replaced ("partner must bid again" → game-forcing explanation); detRSuit jump shift priority fix — check moved before 4-card support block so hands with 4+ support + qualifying side suit jump shift correctly; 4 new jump shift tests; detRTD bug 3a — bal() check added to 2NT branch; detRTD bug 3b — 4-card majors prioritised over longer minors in minimum response; 2 new detRTD tests (141 total); BB1 competitive filter — hasOppBid check added at all 3 genQ call sites; describeBidInContext td/td_str now returns fixed conventional text (not hand-specific); About tab added; Flag button label dynamic ("Flag hand"/"Flag hands"); dialog title matches.
 *              v87: BB1/BB2 weak two and pre-empt filter — any question where the correct bid is 2H/2S/2D or 3-level+ is rejected and redealt for bb1 and bb2 focus (mirrors existing slam filter, now supersedes it); Flag dialog fixes — Escape closes dialog; mandatory comment with inline error message; auto-close 1.5 s after success; hand fields include correct-bid and user-bid explanation text from assess().
 *              v86: Flag button and modal dialog in debrief panel — collects version, focus, coach text, auction, and hand strings; POSTs to /api/flag (Netlify function → Airtable); netlify/functions/flag.js created; redirect added to netlify.toml.
 *              v85: text sweep — suit symbols replaced with spelled-out names (spades/hearts/diamonds/clubs) in all engine reason strings, describeBidInContext, and whyNot; trailing periods stripped from bid description lines via noPeriod() at describeBidInContext call sites; describeBidInContext opening-context fix — 1-level suit bid in empty auction now returns opening bid text; 4-4-4-1 detOpen fix — singleton C → 1H (was 1D), singleton S → 1D (was 1H) per spec; 4-4-4-1 reason text added; 3 new tests (singleton H/D/S), existing singleton C test corrected (135 total).
 *              v84: fix ESLint error — undefined 'cat' variable in whyNot jump-shift block replaced with id.startsWith("rs_")&&id!=="rs_jnew" (introduced by v83 sweep).
 *              v83: consistency sweep of user-visible description strings — HCP always before card count in all describeBidInContext, whyNot, and engine reason strings.
 *              v82: detRSuit jump shift audit — condition changed from (5-card+2 honours) to (6-card OR 5-card+3-card support for opener); hon(h,s,2) requirement removed; description text updated to distinguish the two hand types; describeBidInContext rs_jnew uses engine reason when available, generic text otherwise; sr added to Engine destructure; assess() infers rs_jnew when player bids at jump-shift level; whyNot explains jump shift strength requirement. 4 new tests (132 total).
 *              v81: BB1 scope expanded to include rebid and r_resp2 — genQ filter and StatsBox inScopeCats updated; BB2 scope expanded to include r_1ntoc — genQ newTypes and inScopeCats updated; FOCUS_COMPAT adds "rebid" to bb1; focus dropdown labels updated to accurately reflect each term's content. No engine changes.
 *              v80: BB1/BB2 Stayman removal — detR1NT gains noStayman parameter; gR1 calls detR1NT directly and passes flag; genQ binds noStayman=true for bb1/bb2; FOCUS_COMPAT removes "stayman" from bb1 and bb2; new 6+ clubs weakness takeout branch (r1_wk, 2C) active when noStayman; describeBidInContext handles 2C r1_wk after 1NT. Test file moved into repo: test_engine-v11.js → bridge-trainer/test_engine.js, test.sh updated.
 *              v79: negative double text fix — nd_dbl now shows engine-generated per-hand reason text (suit lengths, HCP) instead of generic fallback; minor single raise fix in competition — detRND was returning 3x instead of 2x for minor suits after opponent overcall; takeout double text SS symbol bug fix — SNAME/SPLURAL inline maps replace broken .slice(0,-1) approach that produced empty strings from Unicode suit symbols.
 *              v78: consolidate takeout double text — engine is now single source of truth for td/td_str explanation text; describeBidInContext reads reason parameter; whyNot simplified.
 *              v77: fix deprecated model string in coach — changed "claude-sonnet-4-20250514" to "claude-sonnet-4-6".
 *              v76: fix whyNot for negative double — when bestBid is Dbl and id is nd_dbl, return specific "negative double" explanation instead of generic suit/balanced hand message; add coach.js error logging — HTTP status, response body, and caught exception are now logged via console.error for Netlify function diagnostics.
 *              v75: improve takeout double explanation text — td now reads "12-15 HCP, short in opponent's suit, support for the other suits — takeout double"; td_str now reads "16+ HCP — too strong to overcall directly. Double first, then bid your suit or NT over partner's response".
 *              v74: fix impossible hand generation — hcpOk() guard added to all generator inner loops so South's deal + setup hands never exceed 40 HCP; fix 4NT Blackwood vs quantitative in suit auctions — new rb_bwood rule used instead of r1_4nt in detRebid game-raise path; fix roc_raise misused for own-suit bid in detROC — new roc_newsuit rule and matching describeBidInContext case.
 *              v73: rb_jraise explanation changed from "16+ HCP" to "16+ total points"; detR1NT 6-card major now uses tp (total points) for game threshold — 11 HCP + distribution counting to 13 total points bids 4S directly; 7-card major game-force also uses tp; vague explanation text sweep — "strong hand", "extra values", "extra strength" replaced with specific HCP/card-count thresholds; coach per-set prompt updated to direct coach away from restating debrief feedback.
 *              v72: HCP/points explanation sweep — single raise now says "total points"; NT rebid thresholds fixed (rb=1NT p=17-18 now correctly bids 2NT not 3NT, 2-level response p=18 message corrected); jump raise explanation updated to "10-12 total points, 4+ card support — limit raise" for majors and "10-12 HCP" for minors; takeout double explanation updated to cover both normal shape requirement and 17+ strong hand exception (double regardless of shape).
 *              v71: fix illegal auction display in gRND and gRTOD — remove spurious W:Pass entry that was landing in South's column.
 *              v70: complete sweep of all generators — prePasses() added to all sharedAuc arrays that were missing it; fixes illegal auction display across multiple hand types.
 *              v69: fix illegal auction in gRND2 — prepend prePasses to sharedAuc so dealer position is correctly anchored
 *              v68: coach per-set prompt tightened — specific to the set played, 2-3
 *              sentences max, no general progress commentary; Pass explanations after
 *              opponent bids now quantify HCP/suit-length thresholds (detOC, detRND,
 *              describeBidInContext passDesc); stats tab shows all categories in scope
 *              for current focus setting (not just tested), sorted by % score descending
 *              (untested at bottom), Needs Improvement section removed.
 *              (Change 3 — minor raise 4+ check — already correct, no change needed.)
 *
 *              v67: coach focus directive validated against current focus setting before
 *              applying; prevents contested auctions appearing in BB1/BB2/BB3 sessions
 *              when coach directive is incompatible.
 *
 *              v66: auctionToText() derives position description from auction array;
 *              mobile terminology (New Hand, Correct!); coach AI disclaimer;
 *              focus dropdown maxWidth fix.
 *
 *              v65: font sizes refactored into FS / FS_MOBILE constants;
 *              @media font-size overrides removed.
 *
 *              v64: coaching toggle added to mobile header card (RatingBadge),
 *              vertically centred alongside level name and rating.
 *
 *              v63: detRTOD restructured — new suit priority at 10+ HCP, pre-emptive
 *              raise reinstated for 4+ support 6-9 HCP, rs_raise→rtod_raise fix;
 *              detRebidTOD and detRND2 implemented; coach UI: cooldown removed,
 *              Clear history button, log capped at 50; generator auto-retry with
 *              null-focus fallback; coach.js socket timeout; assess() raise
 *              inference fix for partner's overcall suit.
 *
 *              v53: LLM Coach Phase 2 — Netlify serverless function; real
 *              Anthropic API calls via /api/coach; per-set auto coach commentary
 *              with abandonment handling; "How am I doing?" full analysis;
 *              focus directive from coach response; prompt caching (ephemeral).
 *
 *              v52: LLM Coach Phase 1 — hand history (HHK) and coach log (CLK)
 *              data stores; coach tab replaces flagged/lessons; "How am I doing,
 *              Coach?" button with 8-hand cooldown; coach message area on debrief.
 *
 *              v50: mobile hand display — widen suit symbol column (14→16) and
 *              increase gap on .hc-row (8px→10px) to prevent crowding on mobile
 *
 *              v49: 8 engine fixes from EBU Acol spec review
 *              1. detOpen: weak two / pre-empt HCP floor lowered from 6 to 5
 *              2. detR1NT: card-length responses (6-card major, 5-card major,
 *                 Stayman) moved before slam responses
 *              3. detRSuit: equal 5+ card suits → bid higher rank first (not up
 *                 the line); up-the-line retained for 4-card suits only
 *              4. detRSuit: 2-level minor response requires 4+ cards (not 5+)
 *              5. detRebid: after 1NT response, 17-18 HCP → jump to 3NT;
 *                 15-16 HCP → 2NT (was a single 15+ → 2NT block)
 *              6. detROC: double raise threshold lowered from 4-card to 3-card
 *                 support (same values as opening bid raises per spec)
 *              7. detRTD: 2NT invitation no longer requires balanced hand
 *              8. detRND: 3-level negative double requires 10+ HCP (not 8+)
 * @release-note IMPORTANT: @version, @date, and @file MUST be updated on every
 *              release. Never skip. Never reuse a version number.
 *
 * @session-handoff-prompt
 *   -----------------------------------------------------------------------
 *   Copy and paste the following as your first message in a new session,
 *   uploading both bridge-trainer-vN.jsx and test_engine.js at the same time:
 *   -----------------------------------------------------------------------
 *
 *   I am uploading bridge-trainer-vN.jsx and test_engine.js, the current
 *   version of an Acol bridge bidding trainer we have been building together.
 *   Please start by reading the file header carefully — it contains the
 *   release checklist, session-start checklist, change log, and this prompt.
 *
 *   Before touching any code:
 *   1. Read the full file header.
 *   2. The engine is in engine.js. Run bash test.sh — no extraction needed.
 *      All tests must pass before we proceed. Confirm the count.
 *   3. Ask me what changes I want in this session.
 *   4. Agree the full change list before writing any code.
 *   5. Make changes one at a time, re-running tests after each engine change.
 *   6. Only release when all tests pass, following the @release-checklist.
 *
 *   Key facts:
 *   - React JSX app, runs in Claude artifact panel, destined for a website.
 *   - Bidding engine is pure JS (no React), fully unit-tested.
 *   - Acol system, EBU Red Book. Stayman and Blackwood only. No transfers.
 *   - You play South, partner North, opponents East/West.
 *   - Storage keys: bt-v5 (stats), bt-d-v5 (disputes), bt-f-v5 (focus),
 *     bt-q-v5 (current question set).
 *   - David is not a coder. Never ask him to run or test code himself.
 *   - Batch changes into single releases. Do not release after every fix.
 *   - Never reuse a version number. Never update a file without incrementing.
 *   - Always call present_files after copying to outputs — mandatory.
 *   -----------------------------------------------------------------------
 *
 * @engine
 *   The bidding engine lives in engine.js. Import it at the top of App.js.
 *   To audit engine against spec: feed engine.js + EBU spec to Claude for review.
 *   Run tests with: bash test.sh (runs node test_engine.js)
 *
 * @release-checklist
 *   1. Run test_engine.js — all tests must pass
 *   2. Increment version number (never reuse a number)
 *   3. Update @file, @version, @date, @changes in header
 *   4. Update @next block: mark resolved items done, carry forward anything
 *      outstanding, always retain the standing deployment note (item 4).
 *   5. cp /home/claude/bridge-trainer.jsx /mnt/user-data/outputs/bridge-trainer-vN.jsx
 *   6. present_files — REQUIRED, file will not appear on RHS without this
 *
 * @session-start-checklist
 *   1. Read this header before touching any code
 *   2. Re-extract engine.js and run test_engine.js to confirm baseline
 *   3. Agree the change list with David before writing any code
 *   4. Make changes, run tests, fix failures before packaging
 *   5. Before releasing: increment @version, update @date (BST), update @file
 */
import React from 'react';
import Engine from './engine.js';
// eslint-disable-next-line import/no-webpack-loader-syntax
import engineSource from '!!raw-loader!./engine.js';
const {detOpen,detR1NT,detRSuit,detR2NT,detR2C,detRWk,detRebid,detOC,detROC,detRTD,detR1NOC,detRResp2,detRND,detRTOD,detRebidND,detRebidTOD,detRND2,hp,sl,bal,BD,bS,bL,SS,SUITS,R,deal,BO,mj,sr,dt}=Engine;
const { useState, useEffect, useCallback, useRef } = React;

const SC={S:"#1a1a2e",H:"#c0392b",D:"#c0392b",C:"#1a1a2e"};
const FS={xs:8,sm:9,base:11,md:12,lg:13,xl:14,xxl:17,hero:18};
const FS_MOBILE={xs:9,sm:11,base:13,md:15,lg:16,xl:17,xxl:19,hero:20};
const SN={S:"spades",H:"hearts",D:"diamonds",C:"clubs"};
const noPeriod=s=>s?s.replace(/\.$/,''):s;


// SCENARIO GENERATORS — You always play South
// Shared-context: same auction, 3 different hands for you
// Per-item: each hand has its own auction (openings, rebids, overcalls)
// HCP sanity check: player hand + all other named hands must not exceed 40 (a full deck).
// This prevents scenarios that are physically impossible (e.g. South 26 HCP when N+E already hold 20).
function hcpOk(south,...others){return hp(south)+others.reduce((s,h)=>s+hp(h),0)<=40}

function gOpen(n,stats={},noWeakTwo=false){
  const auc=[];
  const its=[];for(let i=0;i<n*30&&its.length<n;i++){const a=deal();const res=detOpen(a[0],auc,"S",noWeakTwo);if(res&&res.bid!=="Pass"){const ap=ruleAccept(res,stats);if(its._rej===undefined)its._rej=0;if(its._rej>=50||Math.random()<ap){its.push({hand:a[0],...res});its._rej=0;}else{its._rej++;}}}
  return its.length>=n?{auc:[],pos:auctionToText([],"S"),q:"What do you open?",items:its.slice(0,n),shared:true,dealer:"S"}:null
}

function gR1(n,stats={},noStayman=false){
  for(let i=0;i<80;i++){
    const h=deal();const o=detOpen(h[0]);
    if(o?.bid==="1NT"){
      const auc=[{seat:"N",bid:"1NT"},{seat:"E",bid:"Pass"}];
      const its=[];for(let j=0;j<n*25&&its.length<n;j++){const a=deal();const res=detR1NT(a[0],auc,noStayman);if(res&&hcpOk(a[0],h[0])){const ap=ruleAccept(res,stats);if(its._rej===undefined)its._rej=0;if(its._rej>=50||Math.random()<ap){its.push({hand:a[0],...res});its._rej=0;}else{its._rej++;}}}
      if(its.length>=n)return{auc,pos:auctionToText(auc),q:"What do you respond?",items:its.slice(0,n),shared:true}
    }
  }
  return null
}

function gRS(n,stats={}){
  for(let i=0;i<80;i++){
    const h=deal();const o=detOpen(h[0]);
    if(o&&o.bid[0]==="1"&&o.bid!=="1NT"){
      const auc=[{seat:"N",bid:o.bid},{seat:"E",bid:"Pass"}];
      const its=[];for(let j=0;j<n*25&&its.length<n;j++){const a=deal();const r=route(a[0],auc);if(r.result&&hcpOk(a[0],h[0])){const ap=ruleAccept(r.result,stats);if(its._rej===undefined)its._rej=0;if(its._rej>=50||Math.random()<ap){its.push({hand:a[0],...r.result});its._rej=0;}else{its._rej++;}}}
      if(its.length>=n)return{auc,pos:auctionToText(auc),q:"What do you respond?",items:its.slice(0,n),shared:true}
    }
  }
  return null
}

function gR2N(n,stats={}){
  const auc=[{seat:"N",bid:"2NT"},{seat:"E",bid:"Pass"}];
  const its=[];for(let j=0;j<n*40&&its.length<n;j++){const a=deal();const r=route(a[0],auc);if(r.result&&hp(a[0])<=20){const ap=ruleAccept(r.result,stats);if(its._rej===undefined)its._rej=0;if(its._rej>=50||Math.random()<ap){its.push({hand:a[0],...r.result});its._rej=0;}else{its._rej++;}}}
  return its.length>=n?{auc,pos:auctionToText(auc),q:"What do you respond?",items:its.slice(0,n),shared:true}:null
}

function gR2C(n,stats={}){
  const auc=[{seat:"N",bid:"2C"},{seat:"E",bid:"Pass"}];
  const its=[];for(let j=0;j<n*40&&its.length<n;j++){const a=deal();const r=route(a[0],auc);if(r.result&&hp(a[0])<=17){const ap=ruleAccept(r.result,stats);if(its._rej===undefined)its._rej=0;if(its._rej>=50||Math.random()<ap){its.push({hand:a[0],...r.result});its._rej=0;}else{its._rej++;}}}
  return its.length>=n?{auc,pos:auctionToText(auc),q:"What do you respond?",items:its.slice(0,n),shared:true}:null
}

function gRW(n,stats={}){
  for(let i=0;i<200;i++){
    const h=deal();const o=detOpen(h[0]);
    if(o&&["2D","2H","2S"].includes(o.bid)&&o.rule.id==="op_wk2"){
      const auc=[{seat:"N",bid:o.bid},{seat:"E",bid:"Pass"}];
      const its=[];for(let j=0;j<n*30&&its.length<n;j++){const a=deal();const r=route(a[0],auc);if(r.result&&hcpOk(a[0],h[0])){const ap=ruleAccept(r.result,stats);if(its._rej===undefined)its._rej=0;if(its._rej>=50||Math.random()<ap){its.push({hand:a[0],...r.result});its._rej=0;}else{its._rej++;}}}
      if(its.length>=n)return{auc,pos:auctionToText(auc,undefined,{[o.bid]:BD[o.bid]+" (weak)"}),q:"What do you respond?",items:its.slice(0,n),shared:true}
    }
  }
  return null
}

function gReb(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const o=detOpen(h[0]);
    if(o&&o.bid[0]==="1"&&o.bid!=="1NT"){
      const rr=detRSuit(h[1],o.bid);
      if(rr&&rr.bid!=="Pass"){
        const sharedAuc=[{seat:"S",bid:o.bid},{seat:"W",bid:"Pass"},{seat:"N",bid:rr.bid},{seat:"E",bid:"Pass"}];
        const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&r.fn==="detRebid"&&detOpen(a[0])?.bid===o.bid&&hcpOk(a[0],h[1])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
        if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,"S"),q:"What do you rebid?",items:hands.slice(0,n),shared:true,dealer:"S"}
      }
    }
  }
  return null
}

function gRebND(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const so=detOpen(h[0]);
    if(so&&so.bid!=="Pass"&&so.bid[0]==="1"&&so.bid!=="1NT"){
      const eoc=detOC(h[3],so.bid);
      if(eoc&&eoc.rule?.id==="oc_suit"&&eoc.bid!=="Pass"){
        const nnd=detRND(h[1],so.bid,eoc.bid);
        if(nnd&&nnd.rule?.id==="nd_dbl"){
          const sharedAuc=[{seat:"S",bid:so.bid},{seat:"W",bid:"Pass"},{seat:"N",bid:"Pass"},{seat:"E",bid:eoc.bid},{seat:"S",bid:"Pass"},{seat:"W",bid:"Pass"},{seat:"N",bid:"Dbl"},{seat:"E",bid:"Pass"}];
          const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&r.fn==="detRebidND"&&detOpen(a[0])?.bid===so.bid&&hcpOk(a[0],h[1],h[3])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
          if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,"S"),q:"What do you rebid?",items:hands.slice(0,n),shared:true,dealer:"S"}
        }
      }
    }
  }
  return null
}

function gRebTOD(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const so=detOpen(h[0]);
    if(so&&so.bid!=="Pass"&&so.bid[0]==="1"&&so.bid!=="1NT"){
      const wd=detOC(h[3],so.bid);
      if(wd&&(wd.rule?.id==="td"||wd.rule?.id==="td_str")&&wd.bid==="Dbl"){
        const nr=detRTOD(h[1],so.bid);
        if(nr&&nr.bid!=="Pass"&&nr.rule?.id!=="rtod_rdbl"){
          const sharedAuc=[{seat:"S",bid:so.bid},{seat:"W",bid:"Dbl"},{seat:"N",bid:nr.bid},{seat:"E",bid:"Pass"}];
          const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&r.fn==="detRebidTOD"&&detOpen(a[0])?.bid===so.bid&&hcpOk(a[0],h[1],h[3])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
          if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,"S"),q:"What do you rebid?",items:hands.slice(0,n),shared:true,dealer:"S"}
        }
      }
    }
  }
  return null
}

function gRND2(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const so=detOpen(h[0]);
    if(so&&so.bid!=="Pass"&&so.bid[0]==="1"&&so.bid!=="1NT"){
      const eoc=detOC(h[3],so.bid);
      if(eoc&&eoc.rule?.id==="oc_suit"&&eoc.bid!=="Pass"){
        const nnd=detRND(h[1],so.bid,eoc.bid);
        if(nnd&&nnd.rule?.id==="nd_dbl"){
          const nreb=detRebidND(h[0],so.bid,eoc.bid);
          if(nreb&&nreb.bid!=="Pass"){
            const pre=prePasses("S","N");
            const sharedAuc=[...pre,{seat:"N",bid:so.bid},{seat:"E",bid:eoc.bid},{seat:"S",bid:"Dbl"},{seat:"W",bid:"Pass"},{seat:"N",bid:nreb.bid},{seat:"E",bid:"Pass"}];
            const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&r.fn==="detRND2"&&hcpOk(a[0],h[1],h[3])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
            if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,"S"),q:"What do you bid now?",items:hands.slice(0,n),shared:true,dealer:"S"}
          }
        }
      }
    }
  }
  return null
}

// Build pre-opening passes (from dealer up to but not including opener)
function prePasses(dealer,openerSeat){
  const seats=["N","E","S","W"];
  const di=seats.indexOf(dealer),oi=seats.indexOf(openerSeat);
  const passes=[];let ci=di;
  while(ci!==oi){passes.push({seat:seats[ci],bid:"Pass"});ci=(ci+1)%4}
  return passes
}

function southPassedFirst(dealer,opener){
  const seats=["N","E","S","W"];
  const di=seats.indexOf(dealer),oi=seats.indexOf(opener);
  let ci=di;while(ci!==oi){if(seats[ci]==="S")return true;ci=(ci+1)%4}
  return false
}
function auctionToText(auc,dealer,bidDisplay){
  const NAMES={N:"Partner",E:"East",S:"You",W:"West"};
  const me=s=>s==="S";
  const nm=s=>NAMES[s]||s;
  const bdisp=b=>(bidDisplay&&bidDisplay[b])||BD[b]||b;
  if(!auc||auc.length===0){return dealer==="S"?"You are the dealer.":dealer?nm(dealer)+" is the dealer.":"";}
  const full=[...auc];
  if(dealer){
    const fnpi=full.findIndex(a=>a.bid!=="Pass");
    const dOk=fnpi<0?full.some(a=>a.seat===dealer):full.slice(0,fnpi+1).some(a=>a.seat===dealer);
    if(!dOk)full.unshift({seat:dealer,bid:"Pass"});
  }
  const openE=full.find(a=>a.bid!=="Pass"&&a.bid!=="Dbl"&&a.bid!=="Rdbl");
  const opS=openE?.seat;
  const sameT=s=>opS&&((s==="N"||s==="S")===(opS==="N"||opS==="S"));
  const parts=[];let i=0;
  while(i<full.length){
    if(full[i].bid==="Pass"){
      const run=[];
      while(i<full.length&&full[i].bid==="Pass"){run.push(full[i]);i++;}
      const fid=parts.length===0&&!!dealer&&run[0].seat===dealer;
      if(run.length===1){
        const s=run[0].seat;
        parts.push(fid?(nm(s)+" "+(me(s)?"deal and pass":"deals and passes")+"."):(nm(s)+" "+(me(s)?"pass":"passes")+"."));
      }else if(fid){
        const s=run[0].seat;
        parts.push(nm(s)+" "+(me(s)?"deal and pass":"deals and passes")+".");
        const rest=run.slice(1);
        if(rest.length===1){parts.push(nm(rest[0].seat)+" "+(me(rest[0].seat)?"pass":"passes")+".");}
        else{const ns=rest.map(a=>nm(a.seat));const l=ns.pop();parts.push(ns.join(", ")+" and "+l+" pass.");}
      }else{
        const ns=run.map(a=>nm(a.seat));
        if(ns.length===2){parts.push(ns[0]+" and "+ns[1]+" both pass.");}
        else{const l=ns.pop();parts.push(ns.join(", ")+" and "+l+" all pass.");}
      }
      continue;
    }
    const{seat,bid}=full[i];
    const n=nm(seat),disp=bdisp(bid);
    const prevNP=full.slice(0,i).filter(a=>a.bid!=="Pass");
    const isFirst=prevNP.length===0;
    const fid=parts.length===0&&dealer===seat;
    const myPrev=prevNP.filter(a=>a.seat===seat);
    if(bid==="Rdbl"){parts.push(n+" "+(me(seat)?"redouble":"redoubles")+".");}
    else if(bid==="Dbl"){
      const ps=seat==="N"?"S":seat==="S"?"N":seat==="E"?"W":"E";
      const pOp=prevNP.some(a=>a.seat===ps);
      const oppB=prevNP.some(a=>a.seat!==seat&&a.seat!==ps);
      const dt=(pOp&&oppB)?"negative":"takeout";
      const dv=me(seat)?("double ("+dt+")"):("doubles ("+dt+")");
      parts.push(fid?(n+" deals and "+dv+"."):(n+" "+dv+"."));
    }else{
      let v;
      if(isFirst){v=fid?(me(seat)?"deal and open":"deals and opens"):(me(seat)?"open":"opens");}
      else if(myPrev.length===0&&!sameT(seat)){v=me(seat)?"overcall":"overcalls";}
      else if(myPrev.length===0){v=me(seat)?"respond":"responds";}
      else{v=me(seat)?"rebid":"rebids";}
      parts.push(n+" "+v+" "+disp+".");
    }
    i++;
  }
  return parts.join(" ");
}


function gOC(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const wo=detOpen(h[3]);
    if(wo&&wo.bid!=="Pass"&&wo.bid[0]==="1"){
      const dealers=["N","E","S","W"],dealer=dealers[Math.floor(Math.random()*4)];
      const mustPass=southPassedFirst(dealer,"W");
      const pre=prePasses(dealer,"W");
      const sharedAuc=[...pre,{seat:"W",bid:wo.bid},{seat:"N",bid:"Pass"},{seat:"E",bid:"Pass"}];
      const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&(!mustPass||detOpen(a[0])?.bid==="Pass")&&hcpOk(a[0],h[3])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
      if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,dealer),q:"What do you bid?",items:hands.slice(0,n),shared:true,dealer}
    }
  }
  return null
}

function gROC(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const wo=detOpen(h[3]);
    if(wo&&wo.bid!=="Pass"&&wo.bid[0]==="1"&&wo.bid!=="1NT"){
      const noc=detOC(h[1],wo.bid);
      if(noc&&noc.rule.id==="oc_suit"&&noc.bid!=="Pass"){
        const dealers=["N","E","S","W"],dealer=dealers[Math.floor(Math.random()*4)];
        const mustPass=southPassedFirst(dealer,"W");
        const pre=prePasses(dealer,"W");
        const sharedAuc=[...pre,{seat:"W",bid:wo.bid},{seat:"N",bid:noc.bid},{seat:"E",bid:"Pass"}];
        const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&(!mustPass||detOpen(a[0])?.bid==="Pass")&&hcpOk(a[0],h[1],h[3])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
        if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,dealer),q:"What do you bid?",items:hands.slice(0,n),shared:true,dealer}
      }
    }
  }
  return null
}

function gRND(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const no=detOpen(h[0]);
    if(no&&no.bid!=="Pass"&&no.bid[0]==="1"&&no.bid!=="1NT"){
      const eoc=detOC(h[1],no.bid);
      if(eoc&&eoc.rule?.id==="oc_suit"&&eoc.bid!=="Pass"&&bL(eoc.bid)<=2){
        const dealers=["N","E","S","W"],dealer=dealers[Math.floor(Math.random()*4)];
        const mustPass=southPassedFirst(dealer,"N");
        const pre=prePasses(dealer,"N");
        const sharedAuc=[...pre,{seat:"N",bid:no.bid},{seat:"E",bid:eoc.bid}];
        const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&r.result.bid!=="Pass"&&r.fn==="detRND"&&(!mustPass||detOpen(a[0])?.bid==="Pass")&&hcpOk(a[0],h[0],h[1])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
        if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,dealer),q:"What do you bid?",items:hands.slice(0,n),shared:true,dealer}
      }
    }
  }
  return null
}
function gRTOD(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const no=detOpen(h[0]);
    if(no&&no.bid!=="Pass"&&no.bid[0]==="1"&&no.bid!=="1NT"){
      const eoc=detOC(h[1],no.bid);
      if(eoc&&(eoc.rule?.id==="td"||eoc.rule?.id==="td_str")&&eoc.bid==="Dbl"){
        const dealers=["N","E","S","W"],dealer=dealers[Math.floor(Math.random()*4)];
        const mustPass=southPassedFirst(dealer,"N");
        const pre=prePasses(dealer,"N");
        const sharedAuc=[...pre,{seat:"N",bid:no.bid},{seat:"E",bid:"Dbl"}];
        const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&r.fn==="detRTOD"&&(!mustPass||detOpen(a[0])?.bid==="Pass")&&hcpOk(a[0],h[0],h[1])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
        if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,dealer),q:"What do you bid?",items:hands.slice(0,n),shared:true,dealer}
      }
    }
  }
  return null
}
function gTD(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const wo=detOpen(h[3]);
    if(wo&&wo.bid!=="Pass"&&wo.bid[0]==="1"&&wo.bid!=="1NT"){
      const dealers=["N","E","S","W"],dealer=dealers[Math.floor(Math.random()*4)];
      const mustPass=southPassedFirst(dealer,"W");
      const pre=prePasses(dealer,"W");
      const sharedAuc=[...pre,{seat:"W",bid:wo.bid},{seat:"N",bid:"Pass"},{seat:"E",bid:"Pass"}];
      const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&(r.result.rule?.id==="td"||r.result.rule?.id==="td_str")&&(!mustPass||detOpen(a[0])?.bid==="Pass")&&hcpOk(a[0],h[3])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
      if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,dealer),q:"What do you bid?",items:hands.slice(0,n),shared:true,dealer}
    }
  }
  return null
}

function gRTD(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const wo=detOpen(h[3]);
    if(wo&&wo.bid!=="Pass"&&wo.bid[0]==="1"&&wo.bid!=="1NT"){
      const nd=detOC(h[1],wo.bid);
      if(nd&&(nd.rule.id==="td"||nd.rule.id==="td_str")){
        const dealers=["N","E","S","W"],dealer=dealers[Math.floor(Math.random()*4)];
        const mustPass=southPassedFirst(dealer,"W");
        const pre=prePasses(dealer,"W");
        const sharedAuc=[...pre,{seat:"W",bid:wo.bid},{seat:"N",bid:"Dbl"},{seat:"E",bid:"Pass"}];
        const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&(!mustPass||detOpen(a[0])?.bid==="Pass")&&hcpOk(a[0],h[1],h[3])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
        if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,dealer),q:"What do you bid?",items:hands.slice(0,n),shared:true,dealer}
      }
    }
  }
  return null
}

function gR1NOC(n,stats={}){
  for(let i=0;i<400;i++){
    const h=deal();const wo=detOpen(h[3]);
    if(wo&&wo.bid!=="Pass"&&wo.bid[0]==="1"&&wo.bid!=="1NT"){
      const noc=detOC(h[1],wo.bid);
      if(noc&&noc.rule.id==="oc_1nt"){
        const dealers=["N","E","S","W"],dealer=dealers[Math.floor(Math.random()*4)];
        const mustPass=southPassedFirst(dealer,"W");
        const pre=prePasses(dealer,"W");
        const sharedAuc=[...pre,{seat:"W",bid:wo.bid},{seat:"N",bid:"1NT"},{seat:"E",bid:"Pass"}];
        const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&(!mustPass||detOpen(a[0])?.bid==="Pass")&&hcpOk(a[0],h[1],h[3])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
        if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,dealer),q:"What do you bid?",items:hands.slice(0,n),shared:true,dealer}
      }
    }
  }
  return null
}

function gReb2(n,stats={}){
  for(let i=0;i<600;i++){
    const h=deal();const o=detOpen(h[0]);
    if(o&&o.bid[0]==="1"&&o.bid!=="1NT"){
      const rr=detRSuit(h[1],o.bid);
      if(rr&&rr.bid!=="Pass"){
        const pre=prePasses("N","S");
        const auc1=[...pre,{seat:"S",bid:o.bid},{seat:"W",bid:"Pass"},{seat:"N",bid:rr.bid},{seat:"E",bid:"Pass"}];
        const rb=detRebid(h[0],o.bid,rr.bid,auc1);
        if(rb&&rb.bid!=="Pass"){
          const sharedAuc=[...auc1,{seat:"S",bid:rb.bid},{seat:"W",bid:"Pass"}];
          const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&r.fn==="detRResp2"&&detRSuit(a[0],o.bid)?.bid===rr.bid&&hcpOk(a[0],h[0])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
          if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,"N"),q:"What do you bid now?",items:hands.slice(0,n),shared:true,dealer:"N"}
        }
      }
    }
  }
  return null
}

function gFSF(n,stats={}){
  for(let i=0;i<600;i++){
    const h=deal();const o=detOpen(h[0]);
    if(o&&o.bid[0]==="1"&&o.bid!=="1NT"){
      const rr=detRSuit(h[1],o.bid);
      if(rr&&rr.bid!=="Pass"&&bS(rr.bid)!==bS(o.bid)){
        const pre=prePasses("N","S");
        const auc1=[...pre,{seat:"S",bid:o.bid},{seat:"W",bid:"Pass"},{seat:"N",bid:rr.bid},{seat:"E",bid:"Pass"}];
        const rb=detRebid(h[0],o.bid,rr.bid,auc1);
        if(rb&&rb.bid!=="Pass"&&bS(rb.bid)!==bS(o.bid)&&bS(rb.bid)!==bS(rr.bid)){
          const sharedAuc=[...auc1,{seat:"S",bid:rb.bid},{seat:"W",bid:"Pass"}];
          const hands=[];for(let j=0;j<n*30&&hands.length<n;j++){const a=deal();const r=route(a[0],sharedAuc);if(r.result&&r.fn==="detRResp2"&&hcpOk(a[0],h[0])){const ap=ruleAccept(r.result,stats);if(hands._rej===undefined)hands._rej=0;if(hands._rej>=50||Math.random()<ap){hands.push({hand:a[0],...r.result});hands._rej=0;}else{hands._rej++;}}}
          if(hands.length>=n)return{auc:sharedAuc,pos:auctionToText(sharedAuc,"N"),q:"What do you bid now?",items:hands.slice(0,n),shared:true,dealer:"N"}
        }
      }
    }
  }
  return null
}


// Rule-level weighted acceptance: given a routed result and stats, return
// acceptance probability. Untested rules always accepted; mastered rules
// rarely accepted so the loop keeps dealing for weaker rules.
function ruleAccept(result, stats){
  const id=result?.rule?.id;
  if(!id)return 1;
  const s=stats[id];
  if(!s||s.tested===0)return 1;// untested — always accept
  return Math.max(0.1, 1-(s.correct/s.tested)*0.9);// floor at 0.1 so mastered rules still appear occasionally
}

function genQ(stats,focus,nH=3){
  const noStayman=focus==="bb1"||focus==="bb2";
  const noWeakTwo=focus==="bb1"||focus==="bb2";
  const QT=[
    {type:"opening",   w:3,gen:(n,s)=>gOpen(n,s,noWeakTwo)},
    {type:"r_1nt",     w:3,gen:(n,s)=>gR1(n,s,noStayman)},
    {type:"r_suit",    w:3,gen:gRS},
    {type:"r_2nt",     w:1,gen:gR2N},
    {type:"r_2c",      w:1,gen:gR2C},
    {type:"r_weak",    w:2,gen:gRW},
    {type:"rebid",     w:3,gen:gReb},
    {type:"r_resp2",   w:2,gen:gReb2},
    {type:"r_fsf",     w:1,gen:gFSF},
    {type:"overcall",  w:2,gen:gOC},
    {type:"td",        w:2,gen:gTD},
    {type:"r_oc",      w:2,gen:gROC},
    {type:"r_td",      w:2,gen:gRTD},
    {type:"r_1ntoc",   w:1,gen:gR1NOC},
    {type:"neg_dbl",   w:2,gen:gRND},
    {type:"r_tod",     w:2,gen:gRTOD},
    {type:"reb_nd",    w:2,gen:gRebND},
    {type:"reb_tod",   w:2,gen:gRebTOD},
    {type:"rnd2",      w:2,gen:gRND2},
  ];
  let types=QT;
  let newTypes=new Set();
  if(focus){const f=focus.toLowerCase();
    if(f==="bb1"){types=types.filter(t=>["opening","r_1nt","r_suit","rebid","r_resp2"].includes(t.type));}
    else if(f==="bb2"){const bb1=["opening","r_1nt","r_suit"];newTypes=new Set(["rebid","r_resp2","overcall","td","r_oc","r_td","r_1ntoc"]);types=types.filter(t=>[...bb1,...newTypes].includes(t.type));}
    else if(f==="bb3"){const bb2=["opening","r_1nt","r_suit","rebid","r_resp2","overcall","td","r_oc","r_td"];newTypes=new Set(["r_2nt","r_2c","r_weak","r_1ntoc"]);types=types.filter(t=>[...bb2,...newTypes].includes(t.type));}
    else if(f==="improver"){const bb3=["opening","r_1nt","r_suit","rebid","r_resp2","overcall","td","r_oc","r_td","r_2nt","r_2c","r_weak","r_1ntoc"];newTypes=new Set(["r_fsf","neg_dbl","r_tod","reb_nd","reb_tod","rnd2"]);types=types.filter(t=>[...bb3,...newTypes].includes(t.type));}
    else if(f.includes("uncontested"))types=types.filter(t=>!["overcall","r_oc","td","r_td","r_1ntoc"].includes(t.type));
    else if(f.includes("competitive"))types=types.filter(t=>["overcall","r_oc","td","r_td","r_1ntoc"].includes(t.type));
    else if(f.includes("opening"))types=types.filter(t=>t.type==="opening");
    else if(f.includes("rebid"))types=types.filter(t=>t.type==="rebid"||t.type==="r_resp2"||t.type==="r_fsf");
    else if(f.includes("overcall"))types=types.filter(t=>["overcall","r_oc","td","r_td"].includes(t.type));
    else if(f.includes("preempt"))types=types.filter(t=>t.type==="r_weak"||t.type==="opening");
    else if(f.includes("response"))types=types.filter(t=>t.type==="r_suit"||t.type==="r_resp2");
    else if(f.includes("stayman"))types=types.filter(t=>t.type==="r_1nt"||t.type==="r_2nt"||t.type==="r_2c");
    else if(f.includes("slam"))types=types.filter(t=>t.type==="rebid"||t.type==="r_resp2");}
  if(!types.length)types=QT;
  const wts=types.map(qt=>{const rc=Object.values(R).filter(r=>r.cat===qt.type);let w=0;rc.forEach(r=>{const s=stats[r.id];w+=(!s||s.tested===0)?5:Math.max(1,5-(s.correct/s.tested)*5)});const normW=rc.length?w/rc.length:5;const boost=newTypes.has(qt.type)?3:1;return qt.w*(1+normW)*boost});
  const tw=wts.reduce((a,b)=>a+b,0);if(!tw)return null;
  let r=Math.random()*tw,ch=types[0];for(let i=0;i<types.length;i++){r-=wts[i];if(r<=0){ch=types[i];break}}
  return ch.gen(nH, stats);
}

// =============================================================================
// UNIFIED ASSESSMENT ENGINE
// assess(hand, auction, testBid?) => { bestBid, rule, hand, request, reason,
//   assessment?: { verdict, yourHand, yourRequest, whyNot } }
//
// Single source of truth: routes to the correct engine function, generates
// plain-English descriptions and reasoning. Replaces handDescription() and
// bidMeaning() which are retained only as fallbacks.
// =============================================================================

// Route to the correct engine function for South given the auction
// =============================================================================
// CANONICAL ROUTE + UNIFIED ASSESS
// route(hand, auction) — single source of truth for routing any auction
//   to the correct engine function. Used by generators, auction_sim, and assess.
// assess(hand, auction, testBid?) — single source of truth for all assessment.
// =============================================================================

// Canonical router — returns {fn, result} for any auction position
function route(hand, auction){
  if(!hand)return{fn:"unknown",result:{bid:"Pass",rule:null,reason:"No hand provided."}};
  const bySeat={N:[],E:[],S:[],W:[]};
  (auction||[]).forEach(a=>{if(bySeat[a.seat])bySeat[a.seat].push(a.bid)});
  const nB=bySeat.N,eB=bySeat.E,sB=bySeat.S,wB=bySeat.W;
  const lastNP=bids=>[...bids].reverse().find(b=>b!=="Pass")||null;
  const pLast=lastNP(nB),wLast=lastNP(wB),eLast=lastNP(eB);
  const anyBid=(auction||[]).some(a=>a.bid!=="Pass");
  const mySuit=sB.filter(b=>b!=="Pass");
  const noInterf=wB.every(b=>b==="Pass")&&eB.every(b=>b==="Pass");

  // No bids yet — opening
  if(!anyBid)return{fn:"detOpen",result:detOpen(hand,auction)};

  // Partner (N) opened, uncontested, I haven't bid
  if(pLast&&noInterf&&mySuit.length===0){
    const pOp=nB.find(b=>b!=="Pass");
    if(pOp==="1NT")return{fn:"detR1NT",result:detR1NT(hand,auction)};
    if(pOp==="2NT")return{fn:"detR2NT",result:detR2NT(hand,auction)};
    if(pOp==="2C")return{fn:"detR2C",result:detR2C(hand,auction)};
    if(bL(pOp)===2&&bS(pOp)!=="NT"&&bS(pOp)!=="C")return{fn:"detRWk",result:detRWk(hand,pOp,auction)};
    if(bL(pOp)>=3&&bS(pOp)!=="NT")return{fn:"detRWk",result:detRWk(hand,pOp,auction)};
    if(bL(pOp)===1&&bS(pOp)!=="NT")return{fn:"detRSuit",result:detRSuit(hand,pOp,auction)};
  }

  // I doubled (negative), West passed, North rebid, I act again
  if(mySuit.length===0&&wB.every(b=>b==="Pass")&&bySeat.S.includes("Dbl")&&pLast&&pLast!=="Pass"&&pLast!=="Dbl"&&eLast&&eLast!=="Pass"&&eLast!=="Dbl"){
    const pOp=nB.find(b=>b!=="Pass");
    const eOc=eB.find(b=>b!=="Pass"&&b!=="Dbl");
    if(pOp&&eOc&&bL(pOp)===1&&bS(pOp)!=="NT")
      return{fn:"detRND2",result:detRND2(hand,pOp,eOc,pLast,auction)};
  }

  // Partner (N) opened a suit, E overcalled a suit, I (S) haven't bid — negative double / competitive response
  if(pLast&&pLast!=="Pass"&&pLast!=="Dbl"&&eLast&&eLast!=="Pass"&&eLast!=="Dbl"&&mySuit.length===0&&wB.every(b=>b==="Pass")){
    return{fn:"detRND",result:detRND(hand,pLast,eLast,auction)};
  }

  // Partner (N) opened a suit, E doubled (takeout), I (S) respond (section 12.2)
  if(pLast&&pLast!=="Pass"&&pLast!=="Dbl"&&eLast==="Dbl"&&mySuit.length===0&&wB.every(b=>b==="Pass")){
    const pOp=nB.find(b=>b!=="Pass");
    if(pOp&&bL(pOp)===1&&bS(pOp)!=="NT")return{fn:"detRTOD",result:detRTOD(hand,pOp,auction)};
  }


  // I opened, E overcalled, partner (N) doubled (negative) — opener's rebid (section 12.1 phase 2)
  if(mySuit.length===1&&wB.every(b=>b==="Pass")&&eLast&&eLast!=="Pass"&&eLast!=="Dbl"&&pLast==="Dbl"){
    return{fn:"detRebidND",result:detRebidND(hand,mySuit[0],eLast,auction)};
  }

  // I opened a suit, West doubled (takeout), North responded, I rebid
  if(mySuit.length===1&&wB.some(b=>b==="Dbl")&&pLast&&pLast!=="Pass"&&pLast!=="Dbl"&&eB.every(b=>b==="Pass")){
    const myOpen=mySuit[0];
    if(bL(myOpen)===1&&bS(myOpen)!=="NT")
      return{fn:"detRebidTOD",result:detRebidTOD(hand,myOpen,pLast,auction)};
  }

  if(mySuit.length===1&&noInterf){
    const pNP=nB.filter(b=>b!=="Pass");
    const myOpen=mySuit[0];
    // Opener's rebid: partner has bid once
    if(pNP.length===1)return{fn:"detRebid",result:detRebid(hand,myOpen,pNP[0],auction)};
    if(pNP.length>=2)return{fn:"detRResp2",result:detRResp2(hand,pNP[0],myOpen,pNP[1],auction)};
  }

  // West opened, I overcall/double (N passed, E passed)
  if(wLast&&nB.every(b=>b==="Pass")&&eB.every(b=>b==="Pass")&&mySuit.length===0){
    return{fn:"detOC",result:detOC(hand,wLast,auction)};
  }

  // Partner (N) overcalled a suit, E passed or doubled, respond
  if(wLast&&pLast&&pLast!=="Pass"&&pLast!=="Dbl"&&mySuit.length===0){
    // E may have doubled (penalty) — still respond to partner's overcall
    if(pLast==="1NT")return{fn:"detR1NOC",result:detR1NOC(hand,auction)};
    return{fn:"detROC",result:detROC(hand,pLast,wLast,auction)};
  }

  // Partner (N) doubled (takeout), E passed
  if(wLast&&pLast==="Dbl"&&eB.every(b=>b==="Pass")&&mySuit.length===0){
    return{fn:"detRTD",result:detRTD(hand,wLast,auction)};
  }

  return{fn:"unknown",result:{bid:"Pass",rule:null,reason:"Auction sequence not recognised — passing."}};
}

// describeBidInContext — what a bid conventionally shows (used for both bestBid and testBid)
function describeBidInContext(bid, rule, hand, auction, reason=""){
  if(!bid||bid==="IDK")return{hand:"",request:null};
  const s=bS(bid),lvl=bL(bid),sn=s&&s!=="NT"?SN[s]:"";
  const id=rule?.id||"";
  const l=hand?sl(hand):null;const p=hand?hp(hand):0;
  // Derive opener's first suit from auction (first S bid that isn't Pass/Dbl)
  const openBid=(auction||[]).find(a=>a.seat==="S"&&a.bid!=="Pass"&&a.bid!=="Dbl"&&a.bid!=="Rdbl");
  const openSuit=openBid?bS(openBid.bid):null;
  const openSuitN=openSuit&&openSuit!=="NT"?SS[openSuit]:null;
  // Check if opponents have bid (for stopper references)
  const oppBid=(auction||[]).some(a=>(a.seat==="E"||a.seat==="W")&&a.bid!=="Pass");

  if(bid==="Pass"){
    const passDesc={
      op_pass:"Below opening strength — fewer than 12 HCP and Rule of 20 not met.",
      r1_pass:"0-10 HCP — too weak to respond to partner's 1NT opening.",
      rs_pass:"Fewer than 5 HCP — too weak to respond to partner's suit opening.",
      rw_pass:"No fit and no game prospect — passing partner's weak two.",
      rb_pass:"Minimum hand — accepting partner's limit bid.",
      roc_pass:"Too weak to act over opponent's overcall.",
      r1noc_pass:"Too weak to respond to partner's 1NT overcall — fewer than 7 HCP.",
      oc_pass:"To overcall at the 1-level you need 8–15 HCP and a 5-card suit (2 honours if under 11 HCP); a 2-level overcall needs 10–15 HCP and a 6-card suit. No action available here.",
      td_pass:"A takeout double needs 12+ HCP with shortage in the opponent's suit and 3+ cards in each other suit. No action available.",
      nd_pass:"A negative double needs at least 6 HCP (8 at the 2-level) and 4+ cards in an unbid major; a natural bid needs a 5-card suit. No action available here.",
    };
    return{hand:passDesc[id]||"Passing — no action available with this hand.",request:null};
  }
  if(bid==="Dbl"||bid==="X"){
    if(id==="dbl_1nt")return{hand:"16+ HCP — penalty double of 1NT.",request:null};
    if(id==="td")return{hand:"12-16 HCP, short in opponent's suit, support for the other suits — takeout double, asking partner to bid their best suit.",request:null};
    if(id==="td_str")return{hand:"17+ HCP — strong hand, too strong to overcall. Double first, then bid again over partner's response.",request:null};
    if(id==="nd_dbl"){
      if(reason)return{hand:reason,request:"Negative double — asks partner to bid their best major."};
      return{hand:`${p>=8?"8+":"6-7"} HCP — negative double, showing 4+ cards in the unbid major(s).`,request:"Asks partner to bid a major suit."};
    }
    return{hand:"12+ HCP, short in opponent's suit, 3+ cards in each other suit.",request:"Asks partner to bid their best suit (takeout)."};
  }
  if(bid==="4NT"&&(id==="r1_4nt"||id==="r2n_4nt"))return{hand:"Slam interest.",request:"Blackwood — asks partner how many aces they hold."};
  if(id==="r1_stay"||id==="r2n_stay")return{hand:`${id==="r2n_stay"?"4+":"11+"} HCP with a 4-card major.`,request:"Stayman — asks partner for a 4-card major."};
  if(bid==="2C"&&id==="r1_wk"){const partnerNT=(auction||[]).some(a=>a.seat==="N"&&a.bid==="1NT");if(partnerNT)return{hand:`${p} HCP with ${l?l.C:"6"}+ clubs — weakness takeout, asking partner to pass and play in clubs.`,request:null};}
  if(bid==="2C"&&!id){const partnerNT=(auction||[]).some(a=>a.seat==="N"&&(a.bid==="1NT"||a.bid==="2NT"));if(partnerNT)return{hand:`11+ with a 4-card major.`,request:"Stayman — asks partner for a 4-card major."};}
  if(id==="fsf")return{hand:`10+ HCP — Fourth Suit Forcing. Artificial, does not show ${sn}.`,request:"Asks partner to clarify their hand further."};
  if(id==="r2c_neg")return{hand:"Fewer than 8 HCP — negative response.",request:null};
  if(id==="r2c_pos")return{hand:"8+ HCP — positive response to 2C.",request:null};
  if(id==="op_1nt")return{hand:"12-14 HCP, balanced — opening 1NT.",request:null};
  if(id==="op_2nt")return{hand:"20-22 HCP, balanced — opening 2NT.",request:null};
  if(id==="op_2c")return{hand:"23+ HCP — the strongest opening bid.",request:null};
  if(id==="op_1s")return{hand:`12-19 HCP — opening bid, longest suit (or higher of equal length).`,request:null};
  if(id==="op_r20")return{hand:`10-11 HCP — borderline opening on Rule of 20.`,request:null};
  if(id==="op_pass")return{hand:"Below opening strength — fewer than 12 HCP and Rule of 20 not met.",request:null};
  if(id==="op_wk2")return{hand:`5-10 HCP, 6-card ${sn} suit with 2 honours — weak two.`,request:null};
  if(id==="op_pre")return{hand:`6-9 HCP, 7+ card ${sn} suit — pre-emptive.`,request:null};
  if(id==="rw_2nt")return{hand:"14+ HCP — enquiry about partner's weak two.",request:null};
  if(id==="rw_raise")return{hand:`Support for partner's ${sn} — pre-emptive raise.`,request:null};
  if(id==="rw_pass")return{hand:"No fit, no game — passing partner's weak two.",request:null};

  // NT bids by level and category
  if(s==="NT"){
    const cat=rule?.cat||"";
    if(lvl===1){
      if(id==="rs_1nt")return{hand:"6-9 HCP, balanced, no 4-card major — limit bid.",request:null};
      if(id==="oc_1nt")return{hand:"16-18 HCP, balanced, stopper in opponent's suit — 1NT overcall.",request:null};
      if(id==="roc_1nt")return{hand:`9-12 HCP with stopper in opponent's suit.`,request:null};
      if(id==="rtd_1nt")return{hand:`6-9 HCP, balanced, stopper in opponent's suit.`,request:null};
      if(id==="rb_ntmin")return{hand:`15-16 HCP, balanced — minimum NT rebid.`,request:null};
      if(cat==="r_suit")return{hand:"6-9 HCP, balanced — limit bid.",request:null};
      if(cat==="rebid")return{hand:`15-16 HCP, balanced — minimum NT rebid.`,request:null};
      if(cat==="r_oc")return{hand:oppBid?`9-12 HCP with stopper in opponent's suit.`:"9-12 HCP — NT response.",request:null};
      if(cat==="r_td")return{hand:oppBid?`6-9 HCP, balanced, stopper in opponent's suit.`:"6-9 HCP, balanced — NT response.",request:null};
      return{hand:"Balanced, limited values.",request:null};
    }
    if(lvl===2){
      if(id==="roc_1nt")return{hand:`9-12 HCP with stopper in opponent's suit — NT response.`,request:null};
      if(id==="rtd_2nt")return{hand:`10-12 HCP, balanced, stopper — inviting game.`,request:null};
      if(id==="r1noc_2nt")return{hand:`7-8 HCP, balanced — inviting 3NT.`,request:null};
      if(cat==="r_suit")return{hand:"11-12 HCP, balanced — inviting 3NT.",request:null};
      if(cat==="rebid"||id==="rb_ntjmp")return{hand:`17-18 HCP, balanced — inviting game.`,request:null};
      if(id==="rb_ntmin")return{hand:`15-16 HCP, balanced — minimum NT rebid.`,request:null};
      if(cat==="r_oc")return{hand:`13-14 HCP with double stopper — inviting game.`,request:null};
      if(cat==="r_td")return{hand:`10-12 HCP, balanced, stopper — inviting game.`,request:null};
      if(cat==="r_1ntoc")return{hand:`7-8 HCP, balanced — inviting 3NT.`,request:null};
      return{hand:`11-12 HCP, balanced — inviting game.`,request:null};
    }
    if(lvl===3){
      if(id==="rb_inv")return{hand:"Accepting partner's 2NT invitation — bidding game.",request:null};
      if(id==="rb_ntgm"||id==="rb_ntmin")return{hand:"19+ HCP, balanced — game-going values.",request:null};
      if(id==="r1_3nt")return{hand:`13-18 HCP, balanced — game over partner's 1NT.`,request:null};
      if(id==="rs_3nt")return{hand:`13-15 HCP, balanced — game in no trumps.`,request:null};
      if(id==="rtd_3nt")return{hand:`13-15 HCP, balanced, stopper — game in no trumps.`,request:null};
      if(id==="r2n_3nt")return{hand:`4-10 HCP — game over partner's 2NT.`,request:null};
      if(id==="roc_3nt")return{hand:`15+ HCP, stopper — game in no trumps.`,request:null};
      if(cat==="r_2nt")return{hand:`4-10 HCP — game over partner's 2NT.`,request:null};
      return{hand:`13+ HCP, balanced — game.`,request:null};
    }
    if(lvl===4)return{hand:"Quantitative slam invite.",request:"Asks partner to bid 6NT with a maximum."};
  }

  // Suit bids by rule id
  if(id==="rs_raise")return{hand:`6-9 HCP, ${l?l[s]:3}+ ${sn} support — single raise.`,request:null};
  if(id==="rs_dblr")return{hand:`10-12 HCP with ${l?l[s]:"4"}+ ${sn} support — double raise, inviting game (distribution counts).`,request:null};
  if(id==="rs_gmr"){const distNote=l&&bal(l)?"":" (distribution counts)";return{hand:`13+ HCP with ${l?l[s]:"4"}+ ${sn}${distNote} — game raise.`,request:null};}
  if(id==="rs_new")return{hand:`${lvl===1?"6+":"10+"} HCP, 4+ ${sn}.`,request:null};
  if(id==="rs_jnew"){if(reason)return{hand:reason,request:"Game-forcing — commits the partnership to game. Shows 16+ HCP with a 6-card suit or a 5-card suit with 3-card support for opener. Only use it on a very strong hand."};return{hand:`A jump shift shows 16+ HCP with a 6-card suit or a 5-card suit with 3-card support for partner's suit.`,request:"Game-forcing — commits the partnership to game. Only use it on a very strong hand."};}
  if(id==="rb_own")return{hand:`${lvl<=2?"12-15":"16-18"} HCP, 5+ ${sn} — rebidding own suit.`,request:null};
  if(id==="rb_raise")return{hand:`12-15 HCP, 4+ ${sn} support — raise.`,request:null};
  if(id==="rb_jraise")return{hand:`16+ HCP, 4+ ${sn} support — strong raise.`,request:null};
  if(id==="rb_inv")return{hand:lvl===4?`Accepting partner's invitation — game in ${sn}.`:`Inviting game.`,request:null};
  if(id==="rb_rev")return{hand:openSuitN?`16+ HCP, 5+ ${openSuitN} and 4+ ${sn} — reverse, forcing.`:`16+ HCP, first suit longer and 4+ ${sn} — reverse, forcing.`,request:null};
  if(id==="rb_new")return{hand:openSuitN?`12-15 HCP, 5+ ${openSuitN} and 4+ ${sn} — two-suited hand.`:`12-15 HCP, 4+ ${sn} — new suit, showing shape.`,request:null};
  if(id==="rb_jnew")return{hand:openSuitN?`19+ HCP, 5+ ${openSuitN} and 4+ ${sn} — jump shift, game force.`:`19+ HCP, 4+ ${sn} — jump shift, game force.`,request:null};
  if(id==="oc_suit")return{hand:`8-15 HCP, 5+ ${sn} — overcall.`,request:null};
  if(id==="oc_jump")return{hand:`5-9 HCP, 6+ ${sn} — weak jump overcall.`,request:null};
  if(id==="oc_1nt")return{hand:`16-18 HCP, balanced${oppBid?" with stopper in opponent's suit":""} — 1NT overcall.`,request:null};
  if(id==="td")return{hand:"12-16 HCP, short in opponent's suit, support for the other suits — takeout double, asking partner to bid their best suit.",request:null};
  if(id==="td_str")return{hand:"17+ HCP — strong hand, too strong to overcall. Double first, then bid again over partner's response.",request:null};
  if(id==="roc_raise")return{hand:`6-9 HCP, 3+ ${sn} support — raising partner's overcall.`,request:null};
  if(id==="roc_newsuit")return{hand:`6-9 HCP, 5+ ${sn} — bidding own suit over partner's overcall.`,request:null};
  if(id==="roc_compet")return{hand:`4+ ${sn} support — pre-emptive raise.`,request:null};
  if(id==="roc_dblr"){const nOC=(auction||[]).find(a=>a.seat==="N"&&a.bid!=="Pass"&&a.bid!=="Dbl"&&a.bid!=="Rdbl");const nOCSuit=nOC?bS(nOC.bid):null;return{hand:nOCSuit&&s===nOCSuit?"10-12 HCP — strong raise of partner's overcall.":"10-12 HCP — own suit with good values.",request:null};}
  if(id==="roc_gm")return{hand:`13+ HCP — cue bid, game-forcing.`,request:"Game-forcing enquiry."};
  if(id==="roc_1nt")return{hand:`9-12 HCP, stopper in opponent's suit.`,request:null};
  if(id==="rtd_min")return{hand:`0-8 HCP — minimum response to takeout double, longest suit.`,request:null};
  if(id==="rtd_jump")return{hand:`9-12 HCP, 4+ ${sn} — jump, showing extra values.`,request:null};
  if(id==="rtd_cue")return{hand:`13+ HCP — cue bid, game-forcing.`,request:null};
  if(id==="rtd_1nt")return{hand:oppBid?`6-9 HCP, stopper in opponent's suit.`:"6-9 HCP, balanced — NT response.",request:null};
  if(id==="rtd_2nt")return{hand:`10-12 HCP, balanced, stopper — inviting game.`,request:null};
  if(id==="rtd_3nt")return{hand:`13-15 HCP, balanced, stopper — game.`,request:null};
  if(id==="r1noc_suit")return{hand:`5-10 HCP, 5+ ${sn} — showing a real suit over partner's 1NT overcall.`,request:null};
  if(id==="r1noc_2nt")return{hand:`7-8 HCP, balanced — inviting game.`,request:null};
  if(id==="r1noc_3nt")return{hand:`9+ HCP — game over partner's 1NT overcall.`,request:null};

  if(id==="roc_2nt")return{hand:`13-14 HCP, double stopper in opponent's suit — inviting game.`,request:null};
  if(id==="roc_3nt")return{hand:`15+ HCP, stopper in opponent's suit — game.`,request:null};
  if(id==="roc_pass")return{hand:"Too weak to act — passing partner's overcall.",request:null};
  if(id==="nd_nat")return{hand:`${lvl===1?"6+":"10+"} HCP, 5+ ${sn} — natural bid over opponent's overcall.`,request:null};
  if(id==="nd_pass")return{hand:"Too weak to act over the opponent's overcall — fewer than 6 HCP.",request:null};
  if(id==="rbnd_maj")return{hand:`12+ HCP with 4+ ${sn} — bidding partner's shown major.`,request:null};
  if(id==="rbnd_nt")return{hand:"Stopper in opponent's suit, no major fit — NT rebid.",request:null};
  if(id==="rbnd_own")return{hand:"No major fit — rebidding own long suit.",request:null};
  if(id==="rbnd_pass")return{hand:"Minimum opener, no major fit, no stopper — passing.",request:null};
  if(id==="rtod_rdbl")return{hand:"10+ HCP — redouble, showing good defensive values and suggesting penalties.",request:"Redouble — partner should pass for penalties when possible."};
  if(id==="rtod_2nt")return{hand:"4+ support for partner's suit, game invitational — not a natural NT bid.",request:null};
  if(id==="rtod_raise")return{hand:`Pre-emptive raise — support for partner's ${sn}, making it harder for opponents.`,request:null};
  if(id==="rtod_pass")return{hand:"Too weak to act — passing for now.",request:null};
  if(id==="r1noc_pass")return{hand:"0-6 HCP — too weak to respond to partner's 1NT overcall.",request:null};
  if(id==="oc_pre")return{hand:`6-9 HCP, 7+ ${sn} — pre-emptive double jump overcall.`,request:null};
  if(id==="oc_pass")return{hand:"No suitable action — no good suit, no stopper, no double shape.",request:null};
  if(id==="rb_ntgm")return{hand:"19+ HCP, balanced — game-going values.",request:null};
  if(id==="rb_pass")return{hand:"Minimum opener — accepting partner's limit bid and passing.",request:null};
  if(id==="rs_pass")return{hand:"Too weak to respond — fewer than 5 HCP.",request:null};
  if(id==="rs_1nt")return{hand:"6-9 HCP, balanced, no 4-card major — limit bid.",request:null};
  if(id==="rs_2nt")return{hand:`11-12 HCP, balanced — inviting 3NT.`,request:null};
  if(id==="rs_3nt")return{hand:`13-15 HCP, balanced — game in no trumps.`,request:null};
  if(id==="r1_pass")return{hand:"0-10 HCP — too weak to invite or force. Passing 1NT.",request:null};
  if(id==="r1_2nt")return{hand:"11-12 HCP, balanced — inviting partner to bid 3NT.",request:null};
  if(id==="r1_3nt")return{hand:`13-18 HCP, balanced — game in no trumps.`,request:null};
  if(id==="r1_wk")return{hand:`0-10 HCP, 5+ ${sn} — weak takeout, asking partner to pass.`,request:null};
  if(id==="r1_gm")return{hand:`13+ HCP, 6+ ${sn} — game in the major suit.`,request:null};
  if(id==="r1_3m")return{hand:`11+ HCP, 5+ ${sn} — game-forcing.`,request:"Asks partner to support or bid 3NT."};
  if(id==="r2n_pass")return{hand:"0-3 HCP — too weak to respond even to 2NT.",request:null};
  if(id==="r2n_3nt")return{hand:"4-10 HCP — game over partner's 2NT.",request:null};
  if(id==="r2n_3m")return{hand:`5+ ${sn} — showing shape, game-forcing opposite partner's 2NT.`,request:null};

  // Infer from level/suit when no specific rule
  const isOpening=!(auction||[]).some(a=>a.bid!=="Pass");
  if(lvl===1&&s&&s!=="NT"&&isOpening)return{hand:`12-19 HCP — opening bid, longest suit (or higher of equal length)`,request:null};
  if(lvl===1)return{hand:`6+ HCP, 4+ ${sn}`,request:null};
  if(lvl===2)return{hand:`10+ HCP, 5+ ${sn}`,request:null};
  if(lvl===3)return{hand:`${sn} suit with extra values.`,request:null};
  if(lvl===4&&mj(s))return{hand:`Game in ${sn}.`,request:null};
  return{hand:`${sn} suit.`,request:null};
}

// whyNot — explains why testBid is not preferred over bestBid
// Plain English label for a rule — used in summary bar
function ruleName(rule){
  if(!rule)return"";
  const m={
    op_1nt:"Opening 1NT",op_2nt:"Opening 2NT",op_2c:"Opening 2♣",
    op_wk2:"Weak two",op_pre:"Pre-empt",op_1s:"Opening bid",
    op_r20:"Rule of 20",op_pass:"Pass",
    r1_stay:"Stayman",r1_gm:"Game in major",r1_3m:"Game-forcing suit",
    r1_wk:"Weak takeout",r1_pass:"Pass",r1_2nt:"Invite 2NT",
    r1_3nt:"Game 3NT",r1_4nt:"Quantitative 4NT",
    rs_raise:"Single raise",rs_dblr:"Double raise",rs_gmr:"Game raise",
    rs_new:"New suit",rs_jnew:"Jump shift",rs_1nt:"1NT response",
    rs_2nt:"2NT response",rs_3nt:"3NT response",rs_pass:"Pass",
    r2n_pass:"Pass",r2n_stay:"Stayman",r2n_3m:"5-card major",
    r2n_3nt:"3NT",r2n_4nt:"Slam invite",
    r2c_neg:"Negative 2♦",r2c_pos:"Positive response",
    rw_raise:"Raise pre-empt",rw_2nt:"2NT enquiry",rw_pass:"Pass",
    rb_ntmin:"Minimum NT rebid",rb_ntjmp:"Jump NT rebid",rb_ntgm:"3NT",
    rb_raise:"Raise",rb_jraise:"Strong raise",rb_own:"Rebid suit",
    rb_new:"New suit",rb_rev:"Reverse",rb_jnew:"Jump new suit",
    rb_pass:"Pass",rb_inv:"Invite",
    oc_suit:"Suit overcall",oc_jump:"Jump overcall",oc_1nt:"1NT overcall",
    oc_pre:"Pre-emptive overcall",oc_pass:"Pass",
    td:"Takeout double",td_str:"Strong takeout double",dbl_1nt:"Penalty double",
    roc_pass:"Pass",roc_raise:"Raise overcall",roc_compet:"Competitive raise",
    roc_dblr:"Strong raise",roc_gm:"Game-forcing",
    roc_1nt:"1NT response",roc_2nt:"2NT response",roc_3nt:"3NT",
    rtd_min:"Minimum response",rtd_1nt:"1NT response",rtd_2nt:"2NT response",
    rtd_jump:"Jump response",rtd_cue:"Cue bid",rtd_3nt:"3NT",
    r1noc_pass:"Pass",r1noc_2nt:"2NT invite",r1noc_suit:"Suit response",
    r1noc_3nt:"Game",fsf:"Fourth Suit Forcing",
    nd_dbl:"Negative double",nd_nat:"Natural response",nd_pass:"Pass",
    rbnd_maj:"Bid partner's major",rbnd_nt:"NT rebid",rbnd_own:"Rebid suit",rbnd_pass:"Pass",
    rtod_rdbl:"Redouble",rtod_2nt:"2NT (support)",rtod_raise:"Pre-emptive raise",rtod_pass:"Pass",
  };
  return m[rule.id]||rule.name||rule.id||"";
}

function whyNot(bestResult, testBid, hand, auction){
  const bestBid=bestResult?.bid, bestRule=bestResult?.rule, reason=bestResult?.reason||"";
  const l=sl(hand),p=hp(hand);
  const bs=bS(bestBid),ts=bS(testBid);
  const bsn=bs&&bs!=="NT"?SN[bs]:"",tsn=ts&&ts!=="NT"?SN[ts]:"";
  const id=bestRule?.id||"";

  if(testBid==="Pass"&&bestBid!=="Pass"){
    if(id==="r1_wk")return"With a long suit and a weak hand, bidding is better than passing — you are asking partner to play in your suit, not inviting game.";
    if(id==="td"||id==="td_str"){
      if(reason)return`${reason} Passing lets the opponents play undisturbed.`;
      return"With shortage in the opponent's suit and support for the other suits, a takeout double is the right action — passing lets the opponents play undisturbed.";
    }
    if(id==="roc_dblr"||id==="roc_raise"||id==="roc_compet")return"With support for partner's suit, raising is better than passing — even a pre-emptive raise puts pressure on the opponents.";
    return`Pass is too conservative here. ${reason}`;
  }
  if(bestBid==="Pass"&&testBid!=="Pass")
    return`${BD[testBid]} is too much — ${reason}`;
  if(testBid==="Dbl"&&id!=="td"&&id!=="td_str"&&id!=="dbl_1nt")
    return`A double here would be takeout, promising 3+ in each other suit and shortness in opponent's suit. ${BD[bestBid]} is a better fit for this hand.`;
  if(bestBid==="Dbl"&&testBid!=="Dbl"){
    if(id==="td"||id==="td_str")return null;
    if(id==="nd_dbl")return"With 4+ cards in the unbid major(s), a negative double is the right action — it tells partner you have major suit length without having a suit strong enough to bid naturally.";
    return`${BD[testBid]} shows a suit or balanced hand. But ${reason}`;
  }
  if(bs&&ts===bs&&bL(testBid)>bL(bestBid)){
    if(id==="rw_raise"&&bL(testBid)===4&&mj(bs))
      return`${BD[testBid]} is tempting with 4-card support, but the standard pre-emptive raise is ${BD[bestBid]}. Game is possible with extra shape or shortage — with a flatter hand, keep it at the 3-level.`;
    return`${BD[testBid]} overstates the hand — ${reason}`;
  }
  if(bs&&ts===bs&&bL(testBid)<bL(bestBid))
    return`${BD[testBid]} understates the hand — ${reason}`;
  if(bestBid==="2C"&&(id==="r1_stay"||id==="r2n_stay"))
    return"2♣ here is the Stayman convention, not a club bid — it asks partner to show a 4-card major.";
  if(ts==="NT"&&bs&&bs!=="NT")
    return`${BD[testBid]} suggests a balanced hand with a stopper. But with ${l[bs]||0} ${bsn}, ${BD[bestBid]} is more informative. ${reason}`;
  if(bs==="NT"&&ts&&ts!=="NT")
    return`${BD[testBid]} shows a ${tsn} suit, but ${reason}`;
  // Wrong major: bidding hearts when spades (or vice versa) is correct
  if(mj(bs)&&mj(ts)&&bs!==ts)
    return`With 4 ${bsn} and 4 ${tsn}, bid the higher-ranking major first — ${BD[bestBid]}, not ${BD[testBid]}.`;
  if(id==="rw_2nt"&&ts===bS((auction||[]).find(a=>a.seat==="N"&&a.bid!=="Pass")?.bid))
    return`Over a weak two, bidding partner's suit is a raise, not a new suit. With ${p} HCP, 2NT is the forcing enquiry — ask partner about their suit quality before deciding where to play.`;
  if(id==="rs_raise"&&ts&&ts!=="NT"&&ts!==bs)
    return`With a 5-card suit, show it first — partner can support later. The raise applies when you have 3-card support but no good suit to show.`;
  // Player bids a jump shift in a suit-response context when it's not correct
  if(ts&&ts!=="NT"&&id.startsWith("rs_")&&id!=="rs_jnew"){
    const partnerOpen1L=(auction||[]).find(a=>a.seat==="N"&&a.bid!=="Pass"&&bL(a.bid)===1&&bS(a.bid)!=="NT");
    if(partnerOpen1L){const openSuit=bS(partnerOpen1L.bid);if(ts!==openSuit){const minLvl=sr(ts)>sr(openSuit)?1:2;if(bL(testBid)===minLvl+1)return`${BD[testBid]} is a jump shift — it skips a level and shows 16+ HCP with a 6-card ${SN[ts]||ts} suit or a 5-card suit with 3-card support for partner. With ${p} HCP this hand is not strong enough.`;}}
  }
  // Generic: lead with what's correct and why, not what's wrong
  return`${BD[bestBid]} is correct here — ${reason}`;
}

// Main assess function — single source of truth
function assess(hand, auction, testBid){
  if(!hand)return null;
  const routed=route(hand,auction);
  const result=routed.result||{bid:"Pass",rule:null,reason:""};
  const bestBid=result.bid;
  const rule=result.rule;
  // Engine emits its own reason — use it directly
  const reason=result.reason||"";
  const {hand:_hD,request:_req}=describeBidInContext(bestBid,rule,hand,auction,reason);
  const handDesc=noPeriod(_hD),request=noPeriod(_req);

  const out={
    bestBid, rule, reason,
    marginal:result.marginal||false,
    marginalNote:result.marginalNote||null,
    alternates:result.alternates||null,
    hand:handDesc, request,
    fn:routed.fn,
  };

  if(testBid&&testBid!=="IDK"){
    if(testBid===bestBid){
      const{hand:_yH,request:_yR}=describeBidInContext(bestBid,rule,hand,auction,reason);
      const yH=noPeriod(_yH),yR=noPeriod(_yR);
      out.assessment={verdict:"Correct",yourBid:testBid,yourHand:yH,yourRequest:yR,whyNot:null};
    } else {
      const isAlt=result.alternates&&result.alternates.includes(testBid);
      // For wrong bids, try to infer the rule the user's bid would show
      // by routing a hypothetical hand — use the best-guess rule from context
      const ts=bS(testBid),tl=bL(testBid);
      // Build an inferred rule for common wrong bids so description is meaningful
      let inferredRule=null;
      const cat=rule?.cat||"";
      // Infer Stayman for 3C in response-to-2NT context
      if(testBid==="3C"&&cat==="r_2nt")inferredRule={id:"r2n_stay",cat:"r_2nt",name:"Stayman 2NT"};
      if(ts==="NT"){
        // NT bids: infer from level and category context
        if(tl===1&&cat==="rebid")inferredRule={id:"rb_ntmin",cat:"rebid",name:"NT Min"};
        else if(tl===2&&cat==="rebid")inferredRule={id:"rb_ntjmp",cat:"rebid",name:"NT Jump"};
        else if(tl===1&&cat==="r_suit")inferredRule={id:"rs_1nt",cat:"r_suit",name:"1NT Resp"};
        else if(tl===2&&cat==="r_suit")inferredRule={id:"rs_2nt",cat:"r_suit",name:"2NT Resp"};
        else if(tl===3&&cat==="r_2nt")inferredRule={id:"r2n_3nt",cat:"r_2nt",name:"Raise 3NT"};
      }
      // Infer jump shift: new suit bid at exactly one level above minimum in r_suit context
      if(!inferredRule&&ts&&ts!=="NT"&&cat==="r_suit"){
        const partnerOpen1L=(auction||[]).find(a=>a.seat==="N"&&a.bid!=="Pass"&&bL(a.bid)===1&&bS(a.bid)!=="NT");
        if(partnerOpen1L){const openSuit=bS(partnerOpen1L.bid);if(ts!==openSuit){const minLvl=sr(ts)>sr(openSuit)?1:2;if(tl===minLvl+1)inferredRule={id:"rs_jnew",cat:"r_suit",name:"Jump shift"};}}
      }
      // Infer raise of partner's overcall when user bids North's suit at a higher level
      if(!inferredRule&&ts&&ts!=="NT"){
        const partnerBids=(auction||[]).filter(a=>a.seat==="N"&&a.bid!=="Pass"&&a.bid!=="Dbl"&&a.bid!=="Rdbl");
        if(partnerBids.length){
          const firstPartner=partnerBids[0];
          const firstPartnerIdx=(auction||[]).indexOf(firstPartner);
          const firstPartnerSuit=bS(firstPartner.bid);
          const oppOpenedFirst=(auction||[]).slice(0,firstPartnerIdx).some(a=>(a.seat==="W"||a.seat==="E")&&a.bid!=="Pass");
          if(oppOpenedFirst&&firstPartnerSuit&&firstPartnerSuit!=="NT"&&ts===firstPartnerSuit&&tl>bL(firstPartner.bid)){
            inferredRule=tl>=3?{id:"roc_dblr",cat:"r_oc",name:"Strong raise"}:{id:"roc_raise",cat:"r_oc",name:"Raise overcall"};
          }
        }
      }
      const{hand:_yH2,request:_yR2}=describeBidInContext(testBid,inferredRule,hand,auction);
      const yH=noPeriod(_yH2),yR=noPeriod(_yR2);
      const why=isAlt?
        `${BD[testBid]} is also reasonable — ${result.marginalNote||"both bids are playable"}.`:
        whyNot(result,testBid,hand,auction);
      out.assessment={verdict:isAlt?"Defensible":"Wrong",yourBid:testBid,yourHand:yH,yourRequest:yR,whyNot:why};
    }
  } else if(testBid==="IDK"){
    out.assessment={verdict:"IDK",yourBid:"IDK",yourHand:"",yourRequest:null,whyNot:null};
  }

  return out;
}


function HandCard({hand,bidText,active,idx}){
  if(!hand)return null;
  const isDesktop=window.innerWidth>=640;
  const by={S:[],H:[],D:[],C:[]};hand.forEach(c=>by[c.suit].push(c.rank));
  const p=hp(hand),l=sl(hand);
  return (
    <div style={{flex:"1 1 170px",minWidth:145,padding:8,background:"white",borderRadius:8,border:active?"2px solid var(--acc)":"1px solid var(--bdr)",opacity:active?1:0.75,transition:"all 0.2s"}}>
      <div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",marginBottom:3,fontWeight:600}}>Hand {idx+1}</div>
      {SUITS.map(s => (
        <div key={s} className="hc-row" style={{display:"flex",alignItems:"center",gap:5,lineHeight:1.55,fontSize:(isDesktop?FS:FS_MOBILE).xl}}>
          <span className="hc-suit" style={{color:SC[s],fontWeight:700,fontSize:(isDesktop?FS:FS_MOBILE).xxl,width:16,textAlign:"center"}}>{SS[s]}</span>
          <span className="hc-ranks" style={{color:"var(--fg)",letterSpacing:1,fontFamily:"'JetBrains Mono',monospace",fontSize:(isDesktop?FS:FS_MOBILE).lg}}>{by[s].length?by[s].join(" "):"—"}</span>
        </div>
      ))}
      <div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",marginTop:2,fontFamily:"'JetBrains Mono',monospace"}}>{p} HCP . {dt(l)}</div>
      {bidText&&<div style={{marginTop:4,padding:"3px 6px",borderRadius:4,fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:600,textAlign:"center",background:"var(--s2)",color:"var(--acc)"}}>{bidText}</div>}
    </div>
  );
}

function part3Redundant(part2Hand,part2Req,whyNot){
  if(!whyNot)return false;
  const p2=((part2Hand||"")+" "+(part2Req||"")).toLowerCase().match(/[a-z]{3,}/g)||[];
  const p2w=new Set(p2);if(!p2w.size)return false;
  const p3w=new Set(((whyNot||"").toLowerCase().match(/[a-z]{3,}/g)||[]));
  let n=0;p2w.forEach(w=>{if(p3w.has(w))n++;});
  return n/p2w.size>0.8;
}
function ResultCard({hand,res,idx,auction}){
  if(!hand)return null;
  const isDesktop=window.innerWidth>=640;
  const by={S:[],H:[],D:[],C:[]};hand.forEach(c=>by[c.suit].push(c.rank));
  const p=hp(hand),l=sl(hand);
  // Use assess() as the single source of truth for all descriptions
  let a=null;
  try{ a=res?assess(hand,auction||[],res.userBid):null; }catch(e){ a=null; }
  const isCorrect=res?.correct||false;
  const isMarginal=res?.marginal||false;
  const isIDK=res?.userBid==="IDK";
  return (
    <div style={{flex:"1 1 170px",minWidth:145,padding:8,background:"white",borderRadius:8,border:"1px solid var(--bdr)"}}>
      <div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",marginBottom:3,fontWeight:600}}>Hand {idx+1}</div>
      {SUITS.map(s => (
        <div key={s} className="hc-row" style={{display:"flex",alignItems:"center",gap:5,lineHeight:1.55,fontSize:(isDesktop?FS:FS_MOBILE).xl}}>
          <span className="hc-suit" style={{color:SC[s],fontWeight:700,fontSize:(isDesktop?FS:FS_MOBILE).xxl,width:16,textAlign:"center"}}>{SS[s]}</span>
          <span className="hc-ranks" style={{color:"var(--fg)",letterSpacing:1,fontFamily:"'JetBrains Mono',monospace",fontSize:(isDesktop?FS:FS_MOBILE).lg}}>{by[s].length?by[s].join(" "):"—"}</span>
        </div>
      ))}
      <div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",marginTop:2,fontFamily:"'JetBrains Mono',monospace"}}>{p} HCP . {dt(l)}</div>
      {res&&<div className="res-badge" style={{marginTop:4,padding:"3px 6px",borderRadius:4,fontSize:(isDesktop?FS:FS_MOBILE).base,fontWeight:600,textAlign:"center",
        background:isCorrect?"#e8f5e9":isMarginal?"#fff8e1":"#fde8e8",
        color:isCorrect?"var(--green)":isMarginal?"#8b6914":"var(--red)"}}>
        {isIDK?`You passed on this — correct was ${BD[res.correctBid]||res.correctBid}`:isCorrect?`You bid ${BD[res.userBid]||res.userBid} ✓`:isMarginal?`You bid ${BD[res.userBid]||res.userBid} ~ (also: ${BD[res.correctBid]||res.correctBid})`:`You bid ${BD[res.userBid]||res.userBid} — correct was ${BD[res.correctBid]||res.correctBid}`}
      </div>}
      {a&&<div className="hc-desc" style={{marginTop:4,fontSize:(isDesktop?FS:FS_MOBILE).lg,lineHeight:1.6,color:"var(--dim)"}}>
        {/* IDK: show correct bid description */}
        {isIDK&&<>
          <div style={{marginBottom:3}}>
            <span style={{color:"var(--green)",fontWeight:600}}>{BD[res.correctBid]}:</span>
            {" "}{a.hand||""}
            {a.request&&<span> {a.request}</span>}
          </div>
        </>}
        {/* Correct bid: show description once only */}
        {!isIDK&&isCorrect&&<div style={{color:"var(--dim)"}}>
          {a.hand||""}{a.request&&<span> {a.request}</span>}
        </div>}
        {/* Wrong bid: show your bid description, then correct bid description, then why */}
        {!isIDK&&!isCorrect&&!isMarginal&&<>
          {a.assessment?.yourHand&&<div style={{marginBottom:3,color:"var(--red)"}}>
            <span style={{fontWeight:600}}>{BD[res.userBid]||res.userBid}:</span>
            {" "}{a.assessment.yourHand}
            {a.assessment.yourRequest&&<span> {a.assessment.yourRequest}</span>}
          </div>}
          <div style={{marginBottom:3,color:"var(--green)"}}>
            <span style={{fontWeight:600}}>{BD[res.correctBid]}:</span>
            {" "}{a.hand||""}
            {a.request&&<span> {a.request}</span>}
          </div>
          {a.assessment?.whyNot&&!part3Redundant(a.hand,a.request,a.assessment.whyNot)&&<div style={{color:"var(--fg)",borderTop:"1px solid var(--bdr)",paddingTop:3,marginTop:2}}>
            {a.assessment.whyNot}
          </div>}
        </>}
        {/* Marginal: show your bid description and note */}
        {!isIDK&&isMarginal&&<>
          {a.assessment?.yourHand&&<div style={{marginBottom:3}}>
            <span style={{color:"#8b6914",fontWeight:600}}>{BD[res.userBid]||res.userBid}:</span>
            {" "}{a.assessment.yourHand}
          </div>}
          {a.marginalNote&&<div style={{color:"#8b6914"}}>{a.marginalNote}</div>}
        </>}
      </div>}
    </div>
  );
}

function AuctionBar({auction,dealer}){
  const isDesktop=window.innerWidth>=640;
  const seats=["N","E","S","W"];
  const first=auction&&auction.length?auction[0]?.seat:dealer||"S";
  const si=seats.indexOf(first);
  return (
    <div style={{margin:"4px 0 8px",padding:6,background:"var(--s2)",borderRadius:6}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:2,textAlign:"center"}}>
        {seats.map(s => <div key={s} style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,fontWeight:600,color:s==="N"||s==="S"?"var(--acc)":"var(--red)",padding:"2px 0",borderBottom:"1px solid var(--bdr)"}}>{s==="N"?"N (partner)":s==="S"?"S (you)":s}</div>)}
        {(()=>{
          const cells=[];
          for(let i=0;i<si;i++)cells.push(<div key={"p"+i}/>);
          (auction||[]).forEach((a,i)=>cells.push(<div key={i} style={{padding:"2px",fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:a.bid!=="Pass"&&a.bid!=="Dbl"?600:400,color:a.bid==="Pass"?"var(--dim)":a.bid==="Dbl"?"#c0392b":"var(--acc)"}}>{BD[a.bid]||a.bid}</div>));
          // Pad to S column (index 2 in N/E/S/W)
          const sIdx=2;
          const used=cells.length%4;
          const needed=(sIdx-used+4)%4;
          for(let i=0;i<needed;i++)cells.push(<div key={"pad"+i}/>);
          cells.push(<div key="q" style={{padding:"2px",fontSize:(isDesktop?FS:FS_MOBILE).md,color:"var(--accb)",fontWeight:700}}>?</div>);
          return cells;
        })()}
      </div>
    </div>
  );
}

function BidGrid({onBid,disabled,auction}){
  const isDesktop=window.innerWidth>=640;
  const last=auction?[...auction].reverse().find(a=>a.bid!=="Pass"&&a.bid!=="Dbl"&&a.bid!=="Rdbl"):null;
  const minIdx=last?BO.indexOf(last.bid)+1:1;
  const ds={C:"♣",D:"♦",H:"♥",S:"♠",NT:"NT"},dc={C:"#2d5016",D:"#c0392b",H:"#c0392b",S:"#1a1a2e",NT:"var(--acc)"};
  const lastNP=auction?[...auction].reverse().find(a=>a.bid!=="Pass"):null;
  const canX=!!(lastNP&&(lastNP.seat==="E"||lastNP.seat==="W")&&lastNP.bid!=="Dbl"&&lastNP.bid!=="Rdbl");
  const canXX=!!(lastNP&&(lastNP.seat==="E"||lastNP.seat==="W")&&lastNP.bid==="Dbl");
  const diagBg="linear-gradient(to bottom right, transparent calc(50% - 0.5px), #bbb calc(50% - 0.5px), #bbb calc(50% + 0.5px), transparent calc(50% + 0.5px)), linear-gradient(to bottom left, transparent calc(50% - 0.5px), #bbb calc(50% - 0.5px), #bbb calc(50% + 0.5px), transparent calc(50% + 0.5px)), var(--s2)";
  const illegalSt={padding:"5px 1px",borderRadius:3,border:"1px solid var(--bdr)",background:diagBg,color:"#bbb",cursor:"default",fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:600};
  const legalActionSt={padding:"5px 1px",borderRadius:3,border:"1px solid var(--bdr)",background:"var(--s2)",color:"var(--dim)",cursor:disabled?"default":"pointer",fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:600};
  return (
    <div style={{marginTop:8}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:3}}>
        <button onClick={()=>onBid("Pass")} disabled={disabled} style={{...legalActionSt}}>Pass</button>
        {canX
          ? <button onClick={()=>onBid("Dbl")} disabled={disabled} style={{...legalActionSt}}>Double</button>
          : <button disabled style={{...illegalSt}}>Double</button>}
        {canXX
          ? <button onClick={()=>onBid("Rdbl")} disabled={disabled} style={{...legalActionSt}}>Redouble</button>
          : <button disabled style={{...illegalSt}}>Redouble</button>}
        <div/>
        <button onClick={()=>onBid("IDK")} disabled={disabled} style={{...legalActionSt,color:"var(--amber)"}}>IDK</button>
        {[1,2,3,4,5,6,7].map(lv=>["C","D","H","S","NT"].map(d=>{
          const bid=lv+(d==="NT"?"NT":d),ok=BO.indexOf(bid)>=minIdx;
          return ok
            ? <button key={bid} onClick={()=>onBid(bid)} disabled={disabled} style={{padding:"5px 1px",borderRadius:3,fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:600,border:"1px solid var(--bdr)",background:"var(--s2)",color:dc[d],cursor:disabled?"default":"pointer",fontFamily:"'JetBrains Mono',monospace"}}>{lv}{ds[d]}</button>
            : <button key={bid} disabled style={{padding:"5px 1px",borderRadius:3,fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:600,border:"1px solid var(--bdr)",background:diagBg,color:"#bbb",cursor:"default",fontFamily:"'JetBrains Mono',monospace"}}>{lv}{ds[d]}</button>;
        }))}
      </div>
    </div>
  );
}


const CAT_NAMES={"opening":"Opening bids","r_1nt":"Responses to 1NT","r_suit":"Responses to suit","r_2nt":"Responses to 2NT","r_2c":"Responses to 2♣","r_weak":"Responses to weak two","rebid":"Opener rebids","r_resp2":"Responder's 2nd bid","r_fsf":"Fourth Suit Forcing","overcall":"Overcalls","td":"Takeout doubles","r_oc":"Responses to overcall","r_td":"Responses to double","r_1ntoc":"Responses to 1NT overcall","neg_dbl":"Negative doubles","r_tod":"Responses over double","reb_nd":"Rebid after negative double","reb_tod":"Rebid after takeout double","rnd2":"Responder's rebid after negative double"};

function StatsBox({stats,session,focus}){
  const isDesktop=window.innerWidth>=640;
  const ALL_CATS=["opening","r_1nt","r_suit","r_2nt","r_2c","r_weak","rebid","r_resp2","r_fsf","overcall","td","r_oc","r_td","r_1ntoc","neg_dbl","r_tod","reb_nd","reb_tod","rnd2"];
  // Determine which categories are in scope for the current focus (mirrors genQ logic)
  let inScopeCats;
  if(!focus){inScopeCats=ALL_CATS;}
  else{const f=focus.toLowerCase();
    if(f==="bb1")inScopeCats=["opening","r_1nt","r_suit","rebid","r_resp2"];
    else if(f==="bb2")inScopeCats=["opening","r_1nt","r_suit","rebid","r_resp2","overcall","td","r_oc","r_td","r_1ntoc"];
    else if(f==="bb3")inScopeCats=["opening","r_1nt","r_suit","rebid","r_resp2","overcall","td","r_oc","r_td","r_2nt","r_2c","r_weak","r_1ntoc"];
    else if(f==="improver")inScopeCats=ALL_CATS;
    else if(f.includes("uncontested"))inScopeCats=ALL_CATS.filter(c=>!["overcall","r_oc","td","r_td","r_1ntoc"].includes(c));
    else if(f.includes("competitive"))inScopeCats=["overcall","r_oc","td","r_td","r_1ntoc"];
    else if(f.includes("opening"))inScopeCats=["opening"];
    else if(f.includes("rebid"))inScopeCats=["rebid","r_resp2","r_fsf"];
    else if(f.includes("overcall"))inScopeCats=["overcall","r_oc","td","r_td"];
    else if(f.includes("preempt"))inScopeCats=["r_weak","opening"];
    else if(f.includes("response"))inScopeCats=["r_suit","r_resp2"];
    else if(f.includes("stayman"))inScopeCats=["r_1nt","r_2nt","r_2c"];
    else if(f.includes("slam"))inScopeCats=["rebid","r_resp2"];
    else inScopeCats=ALL_CATS;
  }
  // Aggregate stats by in-scope category
  const cats={};
  inScopeCats.forEach(c=>{cats[c]={t:0,c:0};});
  Object.entries(stats).forEach(([id,st])=>{const r=R[id];if(!r||!cats[r.cat])return;cats[r.cat].t+=st.tested;cats[r.cat].c+=st.correct;});
  // Sort: categories with attempts by % descending; untested categories at bottom
  const sortedCats=Object.entries(cats).sort(([,as],[,bs])=>{
    if(as.t===0&&bs.t===0)return 0;
    if(as.t===0)return 1;
    if(bs.t===0)return -1;
    return(bs.c/bs.t)-(as.c/as.t);
  });
  const tT=Object.values(stats).reduce((s,r)=>s+r.tested,0),tC=Object.values(stats).reduce((s,r)=>s+r.correct,0),pct=tT>0?Math.round(tC/tT*100):0;
  const monoSt={fontFamily:"'JetBrains Mono',monospace",whiteSpace:"nowrap"};
  const colOf=a=>a>=80?"var(--green)":a>=50?"var(--amber)":"var(--red)";
  const sessRow=session.total>0;
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
        <div style={{fontSize:(isDesktop?FS:FS_MOBILE).base,color:"var(--dim)"}}>Overall</div>
        <div style={{...monoSt,fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:700,color:colOf(pct)}}>{pct}% ({tC}/{tT})</div>
      </div>
      {sessRow&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:10}}>
        <div style={{fontSize:(isDesktop?FS:FS_MOBILE).base,color:"var(--dim)"}}>This session</div>
        <div style={{...monoSt,fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:700,color:colOf(Math.round(session.correct/session.total*100))}}>{Math.round(session.correct/session.total*100)}% ({session.correct}/{session.total})</div>
      </div>}
      {!sessRow&&<div style={{marginBottom:10}}/>}
      <div style={{display:"flex",gap:10,marginBottom:10,fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)"}}>
        <span style={{color:"var(--green)"}}>&#9632;</span> ≥80%
        <span style={{color:"var(--amber)"}}>&#9632;</span> 50–79%
        <span style={{color:"var(--red)"}}>&#9632;</span> &lt;50%
      </div>
      <div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,fontWeight:600,color:"var(--dim)",marginBottom:4,textTransform:"uppercase",letterSpacing:1}}>By Category</div>
      {sortedCats.map(([c,s])=>{const a=s.t>0?Math.round(s.c/s.t*100):null;return <div key={c} style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",padding:"3px 0",borderBottom:"1px solid var(--bdr)",fontSize:(isDesktop?FS:FS_MOBILE).base}}><span>{CAT_NAMES[c]||c.replace(/_/g," ")}</span><span style={{...monoSt,color:a!==null?colOf(a):"var(--dim)",fontWeight:600}}>{a!==null?`${a}% (${s.c}/${s.t})`:"—"}</span></div>;})}
    </div>
  );
}


// MAIN APP
const SK="bt-v5",FK="bt-f-v5",QK="bt-q-v5",RK="bt-r-v5",HHK="bt-hh-v1",CLK="bt-cl-v1",COK="bt-co-v1";

function withTimeout(promise,ms){
  let timer;
  return Promise.race([
    promise,
    new Promise((_,reject)=>{timer=setTimeout(()=>reject(new Error("Coach timed out after 30 seconds")),ms);})
  ]).finally(()=>clearTimeout(timer));
}

function buildSystemPrompt(){
  return `You are a friendly bridge teacher helping a beginner Acol player. The engine rules below are the primary authority — never contradict them. Where the engine is silent you may draw on your own Acol knowledge. You will be given the student's history of up to 100 hands, their current rating and level, and the set just completed including the precise cards held. The student has already been shown right/wrong and the engine explanation for each hand — do not repeat these. Your primary focus is always the set just played — interpret and contextualise what happened, grounded in the actual cards held. Only refer to history when it adds something genuinely new: for example, a pattern that has appeared multiple times, or a clear improvement. Never repeat criticism the student has already received about previous hands — if they got something wrong before and wrong again now, comment on the current hand, not the history. Keep per-set messages to 2-4 sentences strictly.

ENGINE SOURCE:
${engineSource}`;
}

async function callCoachAPI(messages){
  const controller=new AbortController();
  const timer=setTimeout(()=>controller.abort(),30000);
  try{
    const resp=await fetch("/api/coach",{
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({systemPrompt:buildSystemPrompt(),messages,cacheBreaker:Date.now()}),
      signal:controller.signal
    });
    if(!resp.ok){const t=await resp.text().catch(()=>"");throw new Error(`API ${resp.status}: ${t}`);}
    return resp.json().catch(err=>{console.error("Coach JSON parse error:",err);throw new Error("Coach returned unreadable response");});
  }finally{
    clearTimeout(timer);
  }
}

const LEVELS=[
  {maxR:1030, emoji:"🥚", name:"Egg",      img:"/images/egg.png"},
  {maxR:1064, emoji:"🐣", name:"Chick",    img:"/images/chick.png"},
  {maxR:1102, emoji:"🐭", name:"Mouse",    img:"/images/mouse.png"},
  {maxR:1144, emoji:"🐱", name:"Kitten",   img:"/images/kitten.png"},
  {maxR:1190, emoji:"🐰", name:"Rabbit",   img:"/images/rabbit.png"},
  {maxR:1240, emoji:"🦔", name:"Hedgehog", img:"/images/hedgehog.png"},
  {maxR:1294, emoji:"🐑", name:"Lamb",     img:"/images/lamb.png"},
  {maxR:1352, emoji:"🐕", name:"Labrador", img:"/images/labrador.png"},
  {maxR:1414, emoji:"🦊", name:"Fox",      img:"/images/fox.png"},
  {maxR:1480, emoji:"🐺", name:"Wolf",     img:"/images/wolf.png"},
  {maxR:1550, emoji:"🐻", name:"Bear",     img:"/images/bear.png"},
  {maxR:1624, emoji:"🐯", name:"Tiger",    img:"/images/tiger.png"},
  {maxR:1702, emoji:"🦁", name:"Lion",     img:"/images/lion.png"},
  {maxR:1784, emoji:"🦅", name:"Eagle",    img:"/images/eagle.png"},
  {maxR:Infinity, emoji:"🐉", name:"Dragon", img:"/images/dragon.png"},
];

function getLevel(R){for(const lvl of LEVELS){if(R<lvl.maxR)return lvl;}return LEVELS[LEVELS.length-1];}

function RatingBadge({correct,coachingOn,onToggle}){
  const isDesktop=window.innerWidth>=640;
  const a=getLevel(correct);
  return(
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--bdr)",marginBottom:8}}>
      <img src={a.img} alt={a.name} style={{width:64,height:64,objectFit:"cover",borderRadius:8,flexShrink:0}}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:700,color:"var(--acc)",fontFamily:"var(--sf)"}}>{a.name}</div>
        <div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",marginTop:2}}>Rating: {Math.round(correct)}</div>
      </div>
      {onToggle&&<div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,flexShrink:0}}>
        <span style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)"}}>Coaching</span>
        <button onClick={onToggle} style={{padding:"2px 8px",borderRadius:10,border:"1px solid var(--bdr)",background:coachingOn?"var(--acc)":"var(--s2)",color:coachingOn?"white":"var(--dim)",fontSize:(isDesktop?FS:FS_MOBILE).sm,fontWeight:600,cursor:"pointer",minWidth:32}}>{coachingOn?"ON":"OFF"}</button>
      </div>}
    </div>
  );
}

function LevelUpSplash({from,to,onDone}){
  const isDesktop=window.innerWidth>=640;
  useEffect(()=>{const t=setTimeout(onDone,3500);return()=>clearTimeout(t)},[onDone]);
  const upgraded=LEVELS.indexOf(to)>LEVELS.indexOf(from);
  return(
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,background:"rgba(0,0,0,0.45)"}} onClick={onDone}>
      <div style={{background:"var(--s1)",borderRadius:16,padding:"28px 32px",textAlign:"center",maxWidth:280,boxShadow:"0 8px 32px rgba(0,0,0,0.25)",border:"2px solid var(--acc)"}}>
        <img src={to.img} alt={to.name} style={{width:120,height:120,objectFit:"cover",borderRadius:12,marginBottom:8}}/>
        <div style={{fontSize:(isDesktop?FS:FS_MOBILE).hero,fontWeight:700,color:"var(--acc)",fontFamily:"var(--sf)",marginBottom:6}}>
          {upgraded?"Level up!":"Keep going!"}
        </div>
        <div style={{fontSize:(isDesktop?FS:FS_MOBILE).lg,color:"var(--fg)",lineHeight:1.4}}>
          {upgraded
            ? <><strong>{to.name}!</strong><br/><span style={{fontSize:(isDesktop?FS:FS_MOBILE).base,color:"var(--dim)"}}>Up from {from.name} {from.emoji}</span></>
            : <>Back to <strong>{to.name}</strong> for now.<br/><span style={{fontSize:(isDesktop?FS:FS_MOBILE).base,color:"var(--dim)"}}>You can do it!</span></>
          }
        </div>
        <div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",marginTop:12}}>tap to continue</div>
      </div>
    </div>
  );
}

function BmcButton(){
  return(
    <div style={{marginTop:16}}>
      <a href="https://www.buymeacoffee.com/DavidShaffer" target="_blank" rel="noreferrer">
        <img src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" alt="Buy Me A Coffee" style={{height:60,width:217}}/>
      </a>
    </div>
  );
}

function App(){
  const[stats,setStats]=useState({});const[sess,setSess]=useState({total:0,correct:0});
  const[handHistory,setHandHistory]=useState([]);const[coachLog,setCoachLog]=useState([]);
  const[coachMessage,setCoachMessage]=useState("");
  const[coachingOn,setCoachingOn]=useState(false);
  const[pendingFocus,setPendingFocus]=useState(null);// {focus,announcement} or null
  const coachCallIdRef=useRef(0);// incremented on each new call; invalidates stale calls
  const[ratingCorrect,setRatingCorrect]=useState(0);const[levelSplash,setLevelSplash]=useState(null);// {from,to}
  const[qSet,setQSet]=useState(null);const[idx,setIdx]=useState(0);
  const[tab,setTab]=useState("quiz");const[loading,setLoading]=useState(true);
  const[cfm,setCfm]=useState(null);
  const[focus,setFocus]=useState("bb1");
  // Multi-hand: bids[i] = user's bid for hand i, null if not yet bid
  const[bids,setBids]=useState([]);
  // Phase: "bidding" or "debrief"
  const[phase,setPhase]=useState("bidding");
  const[flagOpen,setFlagOpen]=useState(false);
  const[flagComment,setFlagComment]=useState("");
  const[flagStatus,setFlagStatus]=useState(null);// null|"ok"|"err"|"req"
  useEffect(()=>{if(!flagOpen)return;const h=e=>{if(e.key==="Escape"){setFlagOpen(false);setFlagComment("");}};window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h);},[flagOpen]);
  const[isDesktop,setIsDesktop]=useState(window.innerWidth>=640);
  useEffect(()=>{const h=()=>setIsDesktop(window.innerWidth>=640);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h)},[]);

  useEffect(()=>{
    // Guarantee loading clears within 3 seconds even if storage hangs
    const timeout=setTimeout(()=>setLoading(false),3000);
    (async()=>{
      try{const r=await Promise.resolve({value:localStorage.getItem(SK)});if(r?.value)setStats(JSON.parse(r.value))}catch{}
      try{const r=await Promise.resolve({value:localStorage.getItem(HHK)});if(r?.value)setHandHistory(JSON.parse(r.value))}catch{}
      try{const r=await Promise.resolve({value:localStorage.getItem(CLK)});if(r?.value)setCoachLog(JSON.parse(r.value))}catch{}
      try{const r=await Promise.resolve({value:localStorage.getItem(COK)});if(r?.value)setCoachingOn(r.value==="1")}catch{}
      try{const r=await Promise.resolve({value:localStorage.getItem(FK)});if(r?.value&&r.value!==""){setFocus(r.value)}else{setFocus("bb1")}}catch{}
      try{const r=await Promise.resolve({value:localStorage.getItem(QK)});if(r?.value){const q=JSON.parse(r.value);setQSet(q);setPhase("bidding");setBids([]);setIdx(0)}}catch{}
      try{const r=await Promise.resolve({value:localStorage.getItem(RK)});
        if(r?.value){const stored=parseFloat(r.value)||0;setRatingCorrect(stored<100?1000:stored);}
        else{setRatingCorrect(1000);try{await Promise.resolve(localStorage.setItem(RK,"1000"))}catch{}}
      }catch{}
      clearTimeout(timeout);
      setLoading(false);
    })()},[]);

  const sv=async s=>{try{await Promise.resolve(localStorage.setItem(SK, JSON.stringify(s)))}catch{}};
  const svF=async f=>{try{await Promise.resolve(localStorage.setItem(FK, f))}catch{}};
  const svR=async n=>{try{await Promise.resolve(localStorage.setItem(RK, String(n)))}catch{}};

  const next=useCallback(()=>{
    setBids([]);setIdx(0);setPhase("bidding");
    const nH=window.innerWidth<640?1:3;
    let q=null;
    const bb12Ex=b=>{const l=bL(b),s=bS(b);return l>=3||(l===2&&s!=="NT"&&s!=="C");};
    const hasOppBid=q=>q&&((q.auc||[]).some(a=>(a.seat==="W"||a.seat==="E")&&a.bid!=="Pass")||(q.items||[]).some(it=>(it._auc||[]).some(a=>(a.seat==="W"||a.seat==="E")&&a.bid!=="Pass")));
    for(let i=0;i<3&&!q;i++){q=genQ(stats,focus,nH);if(q&&(focus==="bb1"||focus==="bb2")&&q.items?.some(it=>bb12Ex(it.bid)))q=null;if(q&&focus==="bb1"&&hasOppBid(q))q=null;}
    if(!q){for(let i=0;i<3&&!q;i++){q=genQ(stats,null,nH);if(q&&(focus==="bb1"||focus==="bb2")&&q.items?.some(it=>bb12Ex(it.bid)))q=null;if(q&&focus==="bb1"&&hasOppBid(q))q=null;}}
    setQSet(q);
  },[stats,focus]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{if(!loading&&!qSet)next()},[loading]);

  const isShared=qSet?.shared;
  const items=qSet?.items||[];
  const nItems=items.length;
  const auc=isShared?(qSet.auc||[]):(items[idx]?._auc||[]);
  const pos=isShared?(qSet.pos||""):(items[idx]?._pos||"");
  const question=isShared?(qSet.q||"What do you bid?"):(items[idx]?._q||"What do you bid?");

  // Per-set coach call — fires automatically after every debrief
  const runPerSetCoachCall=async(allHH,finalItems,finalBids,rating,levelName)=>{
    // Capture a unique ID for this call. handleNextFromDebrief increments the ref,
    // making any in-flight call's ID stale so its result is silently discarded.
    const callId=++coachCallIdRef.current;
    setCoachMessage(`Coach is thinking… (tap ${window.innerWidth>=640?"New Set":"New Hand"} to move on)`);
    try{
      const hhSummary=allHH.slice(-100).map(h=>`${h.cat||"?"} ${h.ruleId||"?"} you=${h.userBid} correct=${h.correctBid} ok=${h.correct} marginal=${h.marginal}`).join("\n");
      const setSummary=finalItems.map((it,i)=>{
        const ub=finalBids[i]||"Pass";
        const ok=ub===it.bid||(it.alternates&&it.alternates.includes(ub));
        const mg=it.marginal&&!ok&&(it.alternates||[]).includes(ub);
        const by={S:[],H:[],D:[],C:[]};it.hand.forEach(c=>by[c.suit].push(c.rank));
        const handStr=SUITS.map(s=>`${SS[s]} ${by[s].join(" ")||"—"}`).join(" / ");
        return `Hand ${i+1}: ${handStr}\n  Correct: ${BD[it.bid]||it.bid}  You bid: ${BD[ub]||ub}  Result: ${ok?"Correct":mg?"Marginal":"Wrong"}\n  Rule: ${it.rule?.name||""}\n  Reason: ${it.reason||""}`;
      }).join("\n\n");
      const VALID_FOCUS=["bb1","bb2","bb3","improver","opening","response","rebid","overcall","preempt","stayman","slam",""];
      const messages=[{role:"user",content:`Rating: ${Math.round(rating)} (${levelName})\n\nHand history (most recent last, one line per hand):\n${hhSummary||"(none yet)"}\n\nSet just completed:\n${setSummary}\n\nComment on the hands in this set only. Be specific — refer to the actual bids made. Keep it to 2-3 sentences maximum. Do not give general progress assessments or long-term observations; the student has a separate 'How am I doing?' feature for that. The only exception: if this set clearly shows the student has mastered something they previously found difficult, you may note it briefly — but only if the hand history directly supports it. Do not restate what the debrief feedback already showed the student. Your value is in adding insight they could not get from the debrief alone — explain why the principle matters, the underlying concept, or how to think about similar situations in future.\n\nOptionally, if you want to direct the next practice set, end your response with a JSON block like:\n\`\`\`json\n{"focus":"opening","announcement":"Let's practise some opening bids."}\n\`\`\`\nThe focus value must be one of: ${VALID_FOCUS.filter(Boolean).join(", ")}. Omit the JSON block if you have no specific direction.`}];
      const data=await withTimeout(callCoachAPI(messages),30000);
      if(coachCallIdRef.current!==callId)return;// user moved to next set — discard silently
      const rawText=(data&&data.content&&Array.isArray(data.content)&&data.content[0]&&data.content[0].text)||"";
      // Extract optional focus directive
      let focusDirective=null;
      const jsonMatch=rawText.match(/```json\s*([\s\S]*?)\s*```/);
      if(jsonMatch){try{const d=JSON.parse(jsonMatch[1]);if(d&&VALID_FOCUS.includes(d.focus))focusDirective=d;}catch{}}
      const displayText=rawText.replace(/```json[\s\S]*?```/g,"").trim();
      if(displayText){
        setCoachMessage(displayText);
        setCoachLog(prev=>{const n=[...prev,{type:"A",date:new Date().toISOString(),cat:"",ruleId:"",ruleName:"Per-set coach",userBid:"",correctBid:"",reason:displayText}].slice(-100);try{localStorage.setItem(CLK,JSON.stringify(n))}catch{}return n;});
      } else if(coachCallIdRef.current===callId){
        setCoachMessage("Coach had nothing to add this time.");
      }
      if(focusDirective)setPendingFocus(focusDirective);
    }catch(e){
      if(coachCallIdRef.current!==callId)return;// user moved to next set — discard silently
      setCoachingOn(false);
      try{localStorage.setItem(COK,"0")}catch{}
      setCoachMessage("Coach is unavailable right now — coaching has been switched off. You can turn it back on in the Coach tab when you're ready.");
    }
  };

  const handleBid=bid=>{
    if(phase!=="bidding")return;
    const newBids=[...bids];newBids[idx]=bid;setBids(newBids);
    // If all hands bid, go to debrief
    if(idx>=nItems-1){
      // Score all hands
      const ns={...stats};let sessC=0;
      const finalBids=newBids;
      const finalItems=items;
      const newHH=[];const newCL=[];
      finalItems.forEach((it,i)=>{
        const ub=finalBids[i]||"Pass";
        const ok=ub===it.bid||(it.alternates&&it.alternates.includes(ub));
        const mg=it.marginal&&!ok&&(it.alternates||[]).includes(ub);
        const rid=it.rule?.id;if(rid){if(!ns[rid])ns[rid]={tested:0,correct:0,lastTested:null};ns[rid].tested++;
        if(ok||(it.marginal&&(it.alternates||[]).includes(ub)))ns[rid].correct++;
        ns[rid].lastTested=new Date().toISOString();}
        if(ok)sessC++;
        newHH.push({date:new Date().toISOString(),cat:it.rule?.cat||"",ruleId:rid||"",userBid:ub,correctBid:it.bid,correct:ok,marginal:mg});
        if(!ok&&!mg){newCL.push({type:"C",date:new Date().toISOString(),cat:it.rule?.cat||"",ruleId:rid||"",ruleName:ruleName(it.rule),userBid:BD[ub]||ub,correctBid:BD[it.bid]||it.bid,reason:it.reason||""});}
      });
      const allHH=[...handHistory,...newHH].slice(-100);
      setHandHistory(()=>{try{localStorage.setItem(HHK,JSON.stringify(allHH))}catch{}return allHH;});
      if(newCL.length){setCoachLog(prev=>{const n=[...prev,...newCL].slice(-100);try{localStorage.setItem(CLK,JSON.stringify(n))}catch{}return n;});}
      setStats(ns);sv(ns);
      setSess(p=>({total:p.total+finalItems.length,correct:p.correct+sessC}));
      // Compute rating synchronously so we can pass it to runPerSetCoachCall
      let newRv=ratingCorrect<1000?1000:ratingCorrect;
      const fromLvl=getLevel(newRv);
      finalItems.forEach((it,i)=>{
        const ub=finalBids[i]||"Pass";
        const correct=ub===it.bid||(it.alternates&&it.alternates.includes(ub));
        const p=(newRv-1000)/1000;
        if(correct){newRv+=3*(1-p);}else{newRv-=3*p;}
        newRv=Math.min(2000,Math.max(1000,newRv));
      });
      svR(newRv);
      const toLvl=getLevel(newRv);
      if(fromLvl!==toLvl)setLevelSplash({from:fromLvl,to:toLvl});
      setRatingCorrect(newRv);
      setPhase("debrief");
      if(coachingOn)runPerSetCoachCall(allHH,finalItems,finalBids,newRv,toLvl.name);
    } else {
      // Move to next hand
      setIdx(idx+1);
    }
  };

  const goBack=()=>{if(phase==="bidding"&&idx>0)setIdx(idx-1)};

  // Build results for debrief
  const results=items.map((it,i)=>{
    const ub=bids[i];if(ub===undefined||ub===null)return null;
    const ok=ub===it.bid||(it.alternates&&it.alternates.includes(ub));
    const mg=it.marginal&&!ok;
    return{userBid:ub,correctBid:it.bid,correct:ok,marginal:mg,rule:it.rule||null,marginalNote:it.marginalNote};
  });

  const clr=async t=>{if(cfm!==t){setCfm(t);return}if(t==="s"){setStats({});setSess({total:0,correct:0});try{await Promise.resolve(localStorage.removeItem(SK))}catch{}}setCfm(null)};

  const askCoach=async()=>{
    setCoachMessage(`Coach is thinking… (tap ${window.innerWidth>=640?"New Set":"New Hand"} to move on)`);
    try{
      const lvl=getLevel(ratingCorrect);
      const hhSummary=handHistory.slice(-100).map(h=>`${h.cat||"?"} ${h.ruleId||"?"} you=${h.userBid} correct=${h.correctBid} ok=${h.correct}`).join("\n");
      const messages=[{role:"user",content:`Rating: ${Math.round(ratingCorrect)} (${lvl.name})\n\nHand history (most recent last, one line per hand):\n${hhSummary||"(none yet)"}\n\nGive a fuller analysis of the student's recent progress — strengths, weaknesses, trends. Comment naturally on their rating and level. Write 1-3 paragraphs.`}];
      const data=await withTimeout(callCoachAPI(messages),30000);
      const text=(data&&data.content&&Array.isArray(data.content)&&data.content[0]&&data.content[0].text)||"";
      if(text){
        setCoachMessage(text);
        setCoachLog(prev=>{const n=[...prev,{type:"B",date:new Date().toISOString(),cat:"",ruleId:"",ruleName:"Ask Coach",userBid:"",correctBid:"",reason:text}].slice(-100);try{localStorage.setItem(CLK,JSON.stringify(n))}catch{}return n;});
      } else {
        setCoachMessage("Coach is unavailable right now — try again later.");
      }
    }catch(e){
      setCoachingOn(false);
      try{localStorage.setItem(COK,"0")}catch{}
      setCoachMessage("Coach is unavailable right now — coaching has been switched off. You can turn it back on in the Coach tab when you're ready.");
    }
  };

  const sendFlag=async()=>{
    if(!flagComment.trim()){setFlagStatus("req");return;}
    setFlagStatus(null);
    const sharedAuc=isShared?(qSet?.auc||[]):[];
    const aucStr=sharedAuc.map(e=>`${e.seat}:${BD[e.bid]||e.bid}`).join(" ");
    const handStrs=items.map((it,i)=>{
      const by={S:[],H:[],D:[],C:[]};it.hand.forEach(c=>by[c.suit].push(c.rank));
      const cards=SUITS.map(s=>`${SS[s]} ${by[s].join(" ")||"—"}`).join(" ");
      const ub=bids[i]||"Pass";
      const itAuc=isShared?(qSet?.auc||[]):(it._auc||[]);
      let a=null;try{a=assess(it.hand,itAuc,ub);}catch{}
      const correctDesc=a?.hand||"";
      const yourDesc=a?.assessment?.yourHand||"";
      return `${cards} | Correct: ${BD[it.bid]||it.bid}${correctDesc?" — "+correctDesc:""} | You bid: ${BD[ub]||ub}${yourDesc?" — "+yourDesc:""}`;
    });
    const body={version:"v93",focus,comment:flagComment,coachText:coachMessage,auction:aucStr,hand1:handStrs[0]||"",hand2:handStrs[1]||"",hand3:handStrs[2]||""};
    try{
      const resp=await fetch("/api/flag",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
      if(resp.ok){setFlagStatus("ok");setTimeout(()=>{setFlagOpen(false);setFlagComment("");},1500);}
      else setFlagStatus("err");
    }catch{setFlagStatus("err");}
  };

  // Topics allowed per structured focus level. For focus values not in this map
  // (topic-specific options like "opening", "competitive", or "all topics"), the
  // coach directive is always accepted — the user has already chosen a specific or
  // unrestricted topic so the coach cannot make the selection worse.
  const FOCUS_COMPAT={
    bb1:      ["opening","response","rebid"],
    bb2:      ["opening","response","rebid","overcall","uncontested","competitive"],
    bb3:      ["opening","response","stayman","rebid","overcall","uncontested","competitive","preempt"],
    improver: ["opening","response","stayman","rebid","overcall","uncontested","competitive","preempt","slam"],
  };

  // handleNextFromDebrief — called by "New Set →" in debrief phase
  // Handles: abandonment of in-flight coach call, pendingFocus usage
  const handleNextFromDebrief=()=>{
    coachCallIdRef.current++;// invalidate any in-flight coach call for this set
    setCoachMessage("");
    const pf=pendingFocus;
    setPendingFocus(null);
    setBids([]);setIdx(0);setPhase("bidding");
    let focusToUse=focus;
    if(pf){
      const allowed=FOCUS_COMPAT[focus];
      if(!allowed||allowed.includes(pf.focus)){focusToUse=pf.focus;}
      // else: pf.focus is incompatible with the current level — discard silently
    }
    const nH=window.innerWidth<640?1:3;
    let q=null;
    const bb12Ex=b=>{const l=bL(b),s=bS(b);return l>=3||(l===2&&s!=="NT"&&s!=="C");};
    const hasOppBid=q=>q&&((q.auc||[]).some(a=>(a.seat==="W"||a.seat==="E")&&a.bid!=="Pass")||(q.items||[]).some(it=>(it._auc||[]).some(a=>(a.seat==="W"||a.seat==="E")&&a.bid!=="Pass")));
    for(let i=0;i<3&&!q;i++){q=genQ(stats,focusToUse,nH);if(q&&(focusToUse==="bb1"||focusToUse==="bb2")&&q.items?.some(it=>bb12Ex(it.bid)))q=null;if(q&&focusToUse==="bb1"&&hasOppBid(q))q=null;}
    if(!q){for(let i=0;i<3&&!q;i++){q=genQ(stats,null,nH);if(q&&(focusToUse==="bb1"||focusToUse==="bb2")&&q.items?.some(it=>bb12Ex(it.bid)))q=null;if(q&&focusToUse==="bb1"&&hasOppBid(q))q=null;}}
    setQSet(q);
  };

  if(loading)return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"Georgia"}}>Loading...</div>;

  return (
    <div style={{"--s1":"#faf8f5","--s2":"#f0ece6","--s3":"#e6e0d8","--bdr":"#d4cdc3","--fg":"#2c2418","--dim":"#8a7e6e","--acc":"#6b4c2a","--accbg":"#f0e6d8","--accb":"#8b5e2f","--green":"#2d7a3a","--red":"#b33030","--amber":"#b8860b","--sf":"'Source Serif 4',Georgia,serif",minHeight:"100vh",background:"var(--s1)",color:"var(--fg)",fontFamily:"var(--sf)"}}>
      {levelSplash&&<LevelUpSplash from={levelSplash.from} to={levelSplash.to} onDone={()=>setLevelSplash(null)}/>}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@300;400;600;700&family=JetBrains+Mono:wght@400;600;700&display=swap');
@media (max-width:639px){
  .hc-row{gap:10px!important}
}`}</style>

      <div style={{padding:"12px 14px 4px",borderBottom:"1px solid var(--bdr)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
          <h1 style={{fontSize:(isDesktop?FS:FS_MOBILE).xxl,fontWeight:700,margin:0}}>Bridge Basics: Acol Bidding Trainer</h1>
          <span style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",fontFamily:"'JetBrains Mono',monospace"}}>{sess.total>0?`${sess.correct}/${sess.total}`:""}</span>
        </div>
        {!isDesktop&&<RatingBadge correct={ratingCorrect} coachingOn={coachingOn} onToggle={()=>{const v=!coachingOn;setCoachingOn(v);try{localStorage.setItem(COK,v?"1":"0")}catch{}}}/>}
        <div style={{display:"flex",gap:0,marginTop:5}}>
          {["quiz","stats","coach","about"].map(t => (
            <button key={t} onClick={()=>{setTab(t);setCfm(null);}} style={{padding:"4px 12px",border:"none",borderBottom:tab===t?"2px solid var(--acc)":"2px solid transparent",background:"none",color:tab===t?"var(--acc)":"var(--dim)",fontSize:(isDesktop?FS:FS_MOBILE).md,fontWeight:tab===t?600:400,cursor:"pointer",fontFamily:"var(--sf)",textTransform:"capitalize"}}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div style={{display:"flex",maxWidth:isDesktop?800:640,margin:"0 auto"}}>
      {isDesktop&&(()=>{const a=getLevel(ratingCorrect);return<div style={{width:150,flexShrink:0,padding:"16px 12px",borderRight:"1px solid var(--bdr)",display:"flex",flexDirection:"column",alignItems:"center",gap:8}}><img src={a.img} alt={a.name} style={{width:120,height:120,objectFit:"cover",borderRadius:10,border:"1px solid var(--bdr)"}}/><div style={{fontSize:(isDesktop?FS:FS_MOBILE).lg,fontWeight:700,color:"var(--acc)",fontFamily:"var(--sf)",textAlign:"center"}}>{a.name}</div><div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",textAlign:"center",lineHeight:1.4}}>Rating: {Math.round(ratingCorrect)}</div><div style={{display:"flex",alignItems:"center",gap:6,marginTop:4}}><span style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)"}}>Coaching</span><button onClick={()=>{const v=!coachingOn;setCoachingOn(v);try{localStorage.setItem(COK,v?"1":"0")}catch{}}} style={{padding:"2px 8px",borderRadius:10,border:"1px solid var(--bdr)",background:coachingOn?"var(--acc)":"var(--s2)",color:coachingOn?"white":"var(--dim)",fontSize:(isDesktop?FS:FS_MOBILE).sm,fontWeight:600,cursor:"pointer",minWidth:32}}>{coachingOn?"ON":"OFF"}</button></div></div>})()}
      <div style={{flex:1,padding:"12px 14px",minWidth:0}}>

        {tab==="quiz"&&<>
          <div style={{display:"flex",gap:4,marginBottom:10,alignItems:"center"}}>
            <span style={{fontSize:(isDesktop?FS:FS_MOBILE).base,color:"var(--dim)",whiteSpace:"nowrap"}}>Focus:</span>
            <select value={focus} onChange={e=>{const v=e.target.value;setFocus(v);svF(v);setBids([]);setIdx(0);setPhase("bidding");
              const nH2=window.innerWidth<640?1:3;const bb12Ex2=b=>{const l=bL(b),s=bS(b);return l>=3||(l===2&&s!=="NT"&&s!=="C");};const hasOppBid2=q=>q&&((q.auc||[]).some(a=>(a.seat==="W"||a.seat==="E")&&a.bid!=="Pass")||(q.items||[]).some(it=>(it._auc||[]).some(a=>(a.seat==="W"||a.seat==="E")&&a.bid!=="Pass")));let q=null;for(let i=0;i<3&&!q;i++){q=genQ({...stats},v,nH2);if(q&&(v==="bb1"||v==="bb2")&&q.items?.some(it=>bb12Ex2(it.bid)))q=null;if(q&&v==="bb1"&&hasOppBid2(q))q=null;}if(!q){for(let i=0;i<3&&!q;i++){q=genQ({...stats},null,nH2);if(q&&(v==="bb1"||v==="bb2")&&q.items?.some(it=>bb12Ex2(it.bid)))q=null;if(q&&v==="bb1"&&hasOppBid2(q))q=null;}}setQSet(q);}}
              style={{flex:1,padding:"5px 8px",borderRadius:5,border:"1px solid var(--bdr)",background:"var(--s2)",color:"var(--fg)",fontSize:(isDesktop?FS:FS_MOBILE).base,fontFamily:"var(--sf)",cursor:"pointer",maxWidth:"100%",boxSizing:"border-box"}}>
              <option value="">All topics</option>
              <option disabled>— Level-based —</option>
              <option value="bb1">Beginning Bridge — Term 1: Openings, responses &amp; rebids</option>
              <option value="bb2">Beginning Bridge — Term 2: Adds competition &amp; 1NT overcall</option>
              <option value="bb3">Beginning Bridge — Term 3: 2NT, 2♣, weak twos, pre-empts &amp; Stayman</option>
              <option value="improver">Improver</option>
              <option disabled>— By topic —</option>
              <option value="uncontested">Uncontested only</option>
              <option value="competitive">Competitive only</option>
              <option value="opening">Openings</option>
              <option value="response">Responses to suit openings</option>
              <option value="rebid">Opener's rebid</option>
              <option value="overcall">Overcalls &amp; takeout doubles</option>
              <option value="preempt">Pre-empts &amp; responses</option>
              <option value="stayman">Responses to NT openings</option>
              <option value="slam">Slam bidding</option>
            </select>
          </div>


          {items.length>0&&<>
            <div className="q-pos" style={{fontSize:(isDesktop?FS:FS_MOBILE).lg,color:"var(--dim)",marginBottom:6,lineHeight:1.4}}>{pos}</div>
            <AuctionBar auction={auc} dealer={qSet?.dealer||(auc.length?auc[0]?.seat:"S")}/>

            {/* BIDDING PHASE */}
            {phase==="bidding"&&<>
              {nItems>1?
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                  {items.map((it,i) => <HandCard key={i} hand={it.hand} bidText={bids[i]?BD[bids[i]]:null} active={i===idx} idx={i}/>)}
                </div>
              :
                <HandCard hand={items[idx]?.hand} active={true} idx={0}/>
              }

              {nItems>1&&<div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                {idx>0&&<button onClick={goBack} style={{padding:"4px 10px",borderRadius:4,border:"1px solid var(--bdr)",background:"var(--s2)",color:"var(--dim)",fontSize:(isDesktop?FS:FS_MOBILE).base,cursor:"pointer"}}>← Back</button>}
                <span style={{fontSize:(isDesktop?FS:FS_MOBILE).base,color:"var(--dim)",fontFamily:"'JetBrains Mono',monospace"}}>Hand {idx+1} of {nItems}</span>
                <button onClick={()=>{next()}} style={{marginLeft:"auto",padding:"4px 10px",borderRadius:4,border:"none",background:"var(--acc)",color:"white",fontSize:(isDesktop?FS:FS_MOBILE).base,fontWeight:600,cursor:"pointer"}}>{isDesktop?"New Set →":"New Hand →"}</button>
              </div>}

              <div className="q-text" style={{fontSize:(isDesktop?FS:FS_MOBILE).xl,fontWeight:600,marginBottom:2}}>{question}</div>
              <BidGrid onBid={handleBid} disabled={false} auction={auc}/>
            </>}

            {/* DEBRIEF PHASE */}
            {phase==="debrief"&&<>
              {nItems>1?
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                  {items.map((it,i) => <ResultCard key={i} hand={it.hand} res={results[i]} idx={i} auction={isShared?auc:(it._auc||[])}/>)}
                </div>
              :
                <ResultCard hand={items[0]?.hand} res={results[0]} idx={0} auction={isShared?auc:(items[0]?._auc||[])}/>
              }

              {/* Summary */}
              {results.some(r=>r&&!r.correct)&&<div style={{marginTop:6,padding:8,borderRadius:6,background:"#fde8e8",border:"1px solid var(--red)"}}>
                {results.map((r,i)=>{if(!r||r.correct)return null;return <div key={i} style={{fontSize:(isDesktop?FS:FS_MOBILE).md,marginBottom:3}}>
                  <strong>Hand {i+1}:</strong> {r.userBid==="IDK"?<>You passed on this one. Correct: <strong>{BD[r.correctBid]}</strong>{r.correctBid!=="Pass"&&r.rule&&<span style={{color:"var(--dim)"}}> ({ruleName(r.rule)})</span>}</>:r.marginal?<>You bid {BD[r.userBid]}. Defensible, textbook is <strong>{BD[r.correctBid]}</strong>.{r.marginalNote&&<span style={{color:"var(--dim)"}}> {r.marginalNote}</span>}</>:<>You bid {BD[r.userBid]}. Correct: <strong>{BD[r.correctBid]}</strong>{r.correctBid!=="Pass"&&r.rule&&<span style={{color:"var(--dim)"}}> ({ruleName(r.rule)})</span>}</>}
                </div>})}
              </div>}
              {results.every(r=>r&&r.correct)&&<div style={{marginTop:6,padding:8,borderRadius:6,background:"#e8f5e9",border:"1px solid var(--green)",fontSize:(isDesktop?FS:FS_MOBILE).lg,fontWeight:600,color:"var(--green)"}}>{isDesktop?"All correct!":"Correct!"}</div>}

              {coachingOn&&coachMessage&&<div style={{marginTop:8,padding:8,borderRadius:6,background:"var(--s2)",border:"1px solid var(--bdr)",fontSize:(isDesktop?FS:FS_MOBILE).md,color:"var(--dim)",lineHeight:1.6,whiteSpace:"pre-wrap"}}><div style={{fontSize:(isDesktop?FS:FS_MOBILE).xs,fontStyle:"italic",marginBottom:6}}>Coach's view — AI can make mistakes.</div>{coachMessage}</div>}

              <div style={{display:"flex",gap:5,marginTop:8,flexWrap:"wrap",alignItems:"center"}}>
                {coachingOn&&<button onClick={askCoach} style={{padding:"6px 12px",borderRadius:5,border:"1px solid var(--bdr)",background:"var(--s2)",color:"var(--acc)",cursor:"pointer",fontSize:(isDesktop?FS:FS_MOBILE).base}}>How am I doing, Coach?</button>}
                <button onClick={()=>{setFlagComment("");setFlagStatus(null);setFlagOpen(true);}} style={{padding:"6px 12px",borderRadius:5,border:"1px solid var(--bdr)",background:"var(--s2)",color:"var(--dim)",cursor:"pointer",fontSize:(isDesktop?FS:FS_MOBILE).base}}>{nItems===1?"Flag hand":"Flag hands"}</button>
                <button onClick={handleNextFromDebrief} style={{padding:"6px 12px",borderRadius:5,border:"none",background:"var(--acc)",color:"white",cursor:"pointer",fontSize:(isDesktop?FS:FS_MOBILE).base,fontWeight:600,flex:1}}>{isDesktop?"New Set →":"New Hand →"}</button>
              </div>

            </>}
          </>}
        </>}

        {tab==="stats"&&<>
          <StatsBox stats={stats} session={sess} focus={focus}/>
          <div style={{marginTop:14}}>
            <button onClick={()=>clr("s")} style={{padding:"5px 10px",borderRadius:4,border:"1px solid var(--red)",background:cfm==="s"?"var(--red)":"none",color:cfm==="s"?"white":"var(--red)",fontSize:(isDesktop?FS:FS_MOBILE).sm,cursor:"pointer"}}>{cfm==="s"?"Confirm":"Reset"}</button>
            {cfm==="s"&&<button onClick={()=>setCfm(null)} style={{marginLeft:5,padding:"5px 10px",borderRadius:4,border:"1px solid var(--bdr)",background:"none",color:"var(--dim)",fontSize:(isDesktop?FS:FS_MOBILE).sm,cursor:"pointer"}}>Cancel</button>}
          </div>
        </>}

        {tab==="coach"&&<div>
          <div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",fontStyle:"italic",marginBottom:8}}>Coach's view — AI can make mistakes.</div>
          <button onClick={()=>{setCoachLog([]);try{localStorage.removeItem(CLK)}catch{}}} style={{padding:"4px 10px",borderRadius:5,border:"1px solid var(--bdr)",background:"var(--s2)",color:"var(--dim)",fontSize:(isDesktop?FS:FS_MOBILE).sm,cursor:"pointer",marginBottom:10,display:"block"}}>Clear coaching history</button>
          {coachingOn&&<button onClick={askCoach} style={{padding:"6px 14px",borderRadius:5,border:"1px solid var(--bdr)",background:"var(--s2)",color:"var(--acc)",cursor:"pointer",fontSize:(isDesktop?FS:FS_MOBILE).base,marginBottom:10,display:"block"}}>How am I doing, Coach?</button>}
          {coachingOn&&coachMessage&&<div style={{marginBottom:10,padding:8,borderRadius:6,background:"var(--s2)",border:"1px solid var(--bdr)",fontSize:(isDesktop?FS:FS_MOBILE).md,color:"var(--dim)",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{coachMessage}</div>}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {coachLog.length===0
              ? <div style={{fontSize:(isDesktop?FS:FS_MOBILE).base,color:"var(--dim)",fontStyle:"italic"}}>No coach entries yet. Complete some sets to build your coaching record.</div>
              : [...coachLog].reverse().slice(0,50).map((e,i)=>{
                  const borderColor=e.type==="A"?"#1a6fa8":e.type==="B"?"#2d7a3a":"#b33030";
                  const catN=CAT_NAMES[e.cat]||e.cat||"";
                  const isCoach=e.type==="A"||e.type==="B";
                  const ts=new Date(e.date).toLocaleString("en-GB",{day:"numeric",month:"long",year:"numeric",hour:"2-digit",minute:"2-digit"});
                  return(
                    <div key={i} style={{padding:"6px 8px",borderRadius:5,background:"var(--s2)",borderLeft:`3px solid ${borderColor}`,fontSize:(isDesktop?FS:FS_MOBILE).base,lineHeight:1.5}}>
                      {isCoach&&<div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,fontWeight:600,color:borderColor,textTransform:"uppercase",letterSpacing:1,marginBottom:3}}>{e.type==="B"?"Full analysis (asked)":"Per-set commentary"}</div>}
                      {isCoach&&e.reason&&<div style={{fontSize:(isDesktop?FS:FS_MOBILE).base,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{e.reason}</div>}
                      {!isCoach&&catN&&<div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,fontWeight:600,color:"var(--dim)",textTransform:"uppercase",letterSpacing:1,marginBottom:2}}>{catN}{e.ruleName?` / ${e.ruleName}`:""}</div>}
                      {!isCoach&&<div>You bid <strong>{e.userBid}</strong> — correct was <strong>{e.correctBid}</strong>.</div>}
                      {!isCoach&&e.reason&&<div style={{color:"var(--dim)",fontSize:(isDesktop?FS:FS_MOBILE).sm,marginTop:2}}>{e.reason}</div>}
                      <div style={{fontSize:(isDesktop?FS:FS_MOBILE).xs,color:"var(--dim)",marginTop:3}}>{ts}</div>
                    </div>
                  );
                })
            }
          </div>
        </div>}

        {tab==="about"&&<div style={{fontSize:(isDesktop?FS:FS_MOBILE).base,lineHeight:1.7,color:"var(--fg)"}}>
          <div style={{fontWeight:700,fontSize:(isDesktop?FS:FS_MOBILE).xl,marginBottom:10}}>Bridge Basics: Acol Bidding Trainer</div>
          <p style={{marginTop:0,marginBottom:10}}>This app was built to help beginner Acol bridge players practise their bidding. It presents hands and asks you to bid them, then explains what the correct bid is and why. Over time it tracks your progress with a rating and level system.</p>
          <p style={{marginTop:0,marginBottom:16}}>The app was developed by David Shaffer, a bridge beginner who is not a professional developer. It was built with the help of Claude, an AI assistant made by Anthropic — without whom it would not exist.</p>
          <div style={{fontWeight:600,fontSize:(isDesktop?FS:FS_MOBILE).lg,marginBottom:8}}>How to use the app</div>
          <div style={{marginBottom:8}}><strong>Focus</strong> — Use the Focus dropdown to concentrate on a particular area of bidding. The Beginning Bridge levels (Terms 1, 2 and 3) follow the EBU Beginning Bridge syllabus. Higher levels introduce more advanced topics.</div>
          <div style={{marginBottom:8}}><strong>Rating and levels</strong> — Your rating starts at 1000 and goes up or down based on your answers. As your rating improves you progress through levels, from beginner through to advanced.</div>
          <div style={{marginBottom:8}}><strong>Coach</strong> — The Coach feature uses AI to give you feedback on your bidding after each set of hands. It can also suggest areas to focus on. The Coach is powered by Claude (Anthropic) and, like all AI, it sometimes makes mistakes. Treat its comments as a helpful guide rather than gospel.</div>
          <div style={{marginBottom:16}}><strong>Flag</strong> — If you think a hand has been dealt incorrectly, the bidding engine has made a mistake, or something doesn't look right, please use the Flag hand button to report it. Your feedback goes directly to the developer and helps improve the app.</div>
          <div style={{fontWeight:600,fontSize:(isDesktop?FS:FS_MOBILE).lg,marginBottom:8}}>A note on accuracy</div>
          <p style={{marginTop:0,marginBottom:8}}>The bidding engine follows the EBU Standard English Acol system. It has been extensively tested but may not be perfect — bridge is a complex game and edge cases exist. If something looks wrong, Flag it.</p>
          <p style={{marginTop:0,marginBottom:0,color:"var(--dim)",fontSize:(isDesktop?FS:FS_MOBILE).sm,fontStyle:"italic"}}>Note to experts: this is an app to help beginners learn how to bid. It does not attempt to model the bidding of more advanced players.</p>
          <BmcButton/>
        </div>}

        <div style={{marginTop:24,paddingTop:10,borderTop:"1px solid var(--bdr)",fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",lineHeight:1.5,fontStyle:"italic",display:"flex",justifyContent:"space-between",alignItems:"flex-end",gap:8}}>
          <span>A learning aid for beginner Acol players. Work in progress — some sequences may be incomplete or incorrect. Always verify with a qualified teacher.</span>
          <span style={{flexShrink:0,fontFamily:"'JetBrains Mono',monospace",fontSize:(isDesktop?FS:FS_MOBILE).xs}}>v93</span>
        </div>

      </div>
      </div>

      {flagOpen&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{background:"var(--s1)",border:"1px solid var(--bdr)",borderRadius:8,padding:20,maxWidth:420,width:"90%",boxShadow:"0 4px 20px rgba(0,0,0,0.15)"}}>
          <div style={{fontWeight:600,marginBottom:8,fontSize:(isDesktop?FS:FS_MOBILE).lg}}>{nItems===1?"Flag this hand":"Flag these hands"}</div>
          <div style={{fontSize:(isDesktop?FS:FS_MOBILE).sm,color:"var(--dim)",marginBottom:10}}>Tell us what seems wrong or surprising.</div>
          <textarea value={flagComment} onChange={e=>setFlagComment(e.target.value)} rows={4} style={{width:"100%",boxSizing:"border-box",padding:"8px",borderRadius:5,border:"1px solid var(--bdr)",fontFamily:"var(--sf)",fontSize:(isDesktop?FS:FS_MOBILE).base,background:"var(--s2)",resize:"vertical"}} placeholder="Describe the problem (required)"/>
          {flagStatus==="req"&&<div style={{marginTop:8,color:"var(--red)",fontSize:(isDesktop?FS:FS_MOBILE).sm}}>Please describe the problem before submitting.</div>}
          {flagStatus==="ok"&&<div style={{marginTop:8,color:"var(--green)",fontSize:(isDesktop?FS:FS_MOBILE).sm}}>Flagged — thank you.</div>}
          {flagStatus==="err"&&<div style={{marginTop:8,color:"var(--red)",fontSize:(isDesktop?FS:FS_MOBILE).sm}}>Could not send — please try again.</div>}
          <div style={{display:"flex",gap:8,marginTop:12,justifyContent:"flex-end"}}>
            <button onClick={()=>setFlagOpen(false)} style={{padding:"6px 14px",borderRadius:5,border:"1px solid var(--bdr)",background:"var(--s2)",color:"var(--dim)",cursor:"pointer",fontSize:(isDesktop?FS:FS_MOBILE).base}}>Cancel</button>
            {flagStatus!=="ok"&&<button onClick={sendFlag} style={{padding:"6px 14px",borderRadius:5,border:"none",background:"var(--acc)",color:"white",cursor:"pointer",fontSize:(isDesktop?FS:FS_MOBILE).base,fontWeight:600}}>Submit</button>}
          </div>
        </div>
      </div>}

    </div>
  );
}

export default App;
